import React, { ReactElement, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useCustomTheme, { lightTheme } from '../../../hooks/theme-hook';
import NavigationButton from '../NavigationButton';
import Loading from '../Loading';
import useTranslation from '../../../hooks/translation-hook';
import { View, Image, Pressable, Platform, ScrollView, Linking, Modal, ImageBackground, Text } from 'react-native';
import Constants from 'expo-constants';
import moment from 'moment';
import useStyles from '../../../hooks/styles-hook';
import { faCircleChevronLeft } from '@fortawesome/free-solid-svg-icons';
import {
  faAward,
  faBasketShopping,
  faBullseye,
  faCalendar,
  faCheck,
  faCheckDouble,
  faChevronDown,
  faChevronUp,
  faChild,
  faCircleExclamation,
  faEyeSlash,
  faMap,
  faPenNib,
  faReceipt,
  faStore,
  faTriangleExclamation,
  faUserSecret,
} from '@fortawesome/pro-regular-svg-icons';
import Label, { LabelType } from '../Label';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';

import pinSmallBlue from '../../../../assets/pin-small-blue.png';
import pinSmallOrange from '../../../../assets/pin-small-orange.png';
import pinSmallGreen from '../../../../assets/pin-small-green.png';
import pinSmallGrey from '../../../../assets/pin-small-grey.png';
import boltIcon from '../../../../assets/bolt-icon.png';
import boltIconBw from '../../../../assets/bolt-icon-bw.png';
import CustomButton from '../CustomButton';
import CustomDatePicker from '../CustomDatePicker';
import CustomTextInput from '../CustomTextInput';
import CustomTimePicker from '../CustomTimePicker';
import { useMutation, useQuery } from '@tanstack/react-query';
import useApi from '../../../hooks/api-hook';
import {
  AcceptMissionReferralRequest,
  AcceptMissionReferralStatus,
  AcceptMissionStatus,
  AcceptMissionViewModel,
  AgeRestrictionType,
  GetAcceptMissionByBranchDto,
  GetAirportTakenCountViewModel,
  GetMissionDetailsDto,
  GetMissionsFromMapDto,
  GeolocationRequirement,
  GetMysteryShopperProfileMemberDto,
  MissionConfigurationDetailType,
  MissionType,
  MissionsClient,
  MysteryShopperClient,
  MyMissionClient,
} from '../../../api/api.g';
import { MapLocation } from '../../../models/MapModels';
import { useAppDispatch, useAppSelector } from '../../../hooks/store-hook';
import { lightBoxActions } from '../../../store/lightBoxSlice';
import { missionsActions } from '../../../store/missionsSlice';
import CustomMarkdown from '../CustomMarkDown';
import MoreInfoButton from '../MoreInfoButton';
import ModalContainer from '../ModalContainer';
import { StringHelper } from '../../../helpers/stringHelper';
import useAdvertisementTrackingHook from '../../../hooks/advertisement-hook';
import { StylesHelper } from '../../../models/helpers/stylesHelper';
import { isMissionAvailableForDiscovery } from '../../../models/helpers/missionAvailabilityHelper';
import { LoggedState } from '../../../models/AuthModelsCommon';
import LoginPopup from '../../login/LoginPopup';
import { drawerActions, DrawerOpenState } from '../../../store/drawerSlice';
import { UserPreferencesState } from '../../../store/userPrefsSlice';
import ComboPopup, { ComboMissionItem } from './MissionComboPopup';
import MissionAirportLocationInfo from './MissionAirportLocationInfo';
import RelatedMinorSelectionPopup from './RelatedMinorSelectionPopup';
import ProfileManager, { ProfileManagerHandles } from '../../myaccount/myprofile/ProfileManager';
import Overlay from '../Overlay';
import AccountVerificationPopup from '../../myaccount/AccountVerificationPopup';
import Popup from '../Popup';
import NotificationPermission from '../../map/NotificationPermission';
import { rootNavigate } from '../../../navigation/rootNavigation';
import type { AirportMissionSearchContext } from './MissionCard';
import NavigationElements from '../../../models/NavigationElements';
import { myAccountActions } from '../../../store/myAccountSlice';
import MissionReferPopup from './MissionReferPopup';
import { isLinkedMysteryShopperRotationLimitReached } from '../../../models/helpers/linkedMysteryShopperRotationStatus';
import GeolocationAcceptanceModal from './GeolocationAcceptanceModal';
import useReferAFriendEnabled from '../../../hooks/refer-a-friend-settings-hook';

interface AirportDestinationOption {
  code: string;
  label: string;
  countryCode: string;
}

const canadaAirportOptions: AirportDestinationOption[] = [
  { code: 'YUL', label: 'Montréal-Trudeau', countryCode: 'CA' },
  { code: 'YYZ', label: 'Toronto Pearson', countryCode: 'CA' },
  { code: 'YVR', label: 'Vancouver Intl', countryCode: 'CA' },
  { code: 'YYC', label: 'Calgary Intl', countryCode: 'CA' },
  { code: 'YOW', label: 'Ottawa Intl', countryCode: 'CA' },
  { code: 'YQB', label: 'Québec Jean Lesage', countryCode: 'CA' },
  { code: 'YHZ', label: 'Halifax Stanfield', countryCode: 'CA' },
  { code: 'YWG', label: 'Winnipeg James Armstrong Richardson', countryCode: 'CA' },
  { code: 'CA-OTHER', label: 'Elsewhere in Canada', countryCode: 'CA' },
];

const otherAirportOptions: AirportDestinationOption[] = [{ code: 'INT-OTHER', label: 'Other international non-Canadian airport', countryCode: 'INT' }];

export interface MissionBranchListProps {
  missionFromMap?: GetMissionsFromMapDto;
  location?: MapLocation;
  airportStartDate?: Date;
  airportSearchContext?: AirportMissionSearchContext;
  profileManagerRef?: React.MutableRefObject<ProfileManagerHandles | null>;
  autoOpenAirportPopupKey?: string;
  autoOpenAirportOpenStep?: 'routeForm';
  autoOpenAirportSource?: 'takeMorePopup' | 'banner';
  onBackPress?(): void;
  onExploreOtherMissionsPress?(): void;
  onAcceptedMission?(missionAssignationId?: string): void;
  onMoreInfoPress?(info: string): void;
  onAdmissibilityPress?(info: string): void;
  onConditionsPress?(info: string): void;
  missionReferralToken?: string;
  referralPeriodMissionConfigurationId?: string;
  onMissionReferralConsumed?(): void;
}

export interface MissionDetailItem {
  dto: GetMissionDetailsDto;
  expanded: boolean;
}

interface MissionReferPopupState {
  periodMissionConfigurationId?: string;
  branchId?: string;
}

type MissionBranchDetailAccordionSection = 'admissibility' | 'conditions';

const shouldExpandMissionByDefault = (missionCount: number, mission: GetMissionDetailsDto) => {
  if (missionCount <= 1) {
    return true;
  }

  if (missionCount > 3) {
    return false;
  }

  if (mission.isTaken) {
    return false;
  }

  if (mission.acceptMissionStatus == null) {
    return true;
  }

  return isMissionAvailableForDiscovery(
    mission,
    mission.acceptMissionStatus === AcceptMissionStatus.MissionEligible || mission.acceptMissionStatus === AcceptMissionStatus.IncompleteProfileRequirements,
  );
};

const normalizeReimbursementExampleAmount = (value: number, direction: 'down' | 'up') => {
  const safeValue = Number.isFinite(value) && value > 0 ? value : 0;

  if (safeValue < 20) {
    return safeValue;
  }

  const roundedValue = direction === 'up' ? Math.ceil(safeValue / 5) * 5 : Math.floor(safeValue / 5) * 5;
  return roundedValue > 0 ? roundedValue : safeValue;
};

const MissionBranchList = (props: MissionBranchListProps) => {
  const localProfileManager = useRef<ProfileManagerHandles | null>(null);
  const profileManager = props.profileManagerRef ?? localProfileManager;
  const theme = useCustomTheme();
  const flashDetailBackground = lightTheme.missionCard?.flashBackgroundLight?.toString() ?? '#fef6e8';
  const flashSummaryBackground = lightTheme.missionCard?.flashBackground?.toString() ?? '#fce6c1';
  const flashTitleColor = lightTheme.text?.titleDark?.toString() ?? '#025164';
  const flashBodyColor = lightTheme.text?.primary?.toString() ?? '#000000';
  const flashErrorColor = lightTheme.text?.error?.toString() ?? '#8e183d';
  const isAirportDarkMode = !!theme.isDark;
  const airportTitleColor = '#1473be';
  const airportBodyColor = '#0d5f99';
  const airportErrorColor = '#0d5f99';
  const airportHeaderBackground = '#b9dff6';
  const airportCardBackground = '#d8edf9';
  const airportFollowUpPopupBackground = '#d8e8f2';
  const airportPopupBackground = isAirportDarkMode ? (theme.background.modal?.toString() ?? '#465866') : '#d8e8f2';
  const airportHeroBackground = isAirportDarkMode ? '#2b5674' : '#0f8de0';
  const airportDrawerBackground = isAirportDarkMode
    ? (theme.background.primary?.toString() ?? '#2b3a46')
    : (lightTheme.background?.primary?.toString() ?? '#ffffff');
  const airportHeaderTitleColor = isAirportDarkMode ? (theme.text.primary?.toString() ?? '#ffffff') : (lightTheme.text?.titleDark?.toString() ?? '#025164');
  const airportHeaderTextColor = isAirportDarkMode ? (theme.text.primary?.toString() ?? '#ffffff') : (lightTheme.text?.primary?.toString() ?? '#000000');
  const airportHeaderLinkColor = isAirportDarkMode
    ? (theme.text.darkTurquoiseTwo?.toString() ?? '#5FC8BA')
    : (lightTheme.text?.darkTurquoiseTwo?.toString() ?? '#035164');
  const airportBackButtonColor = isAirportDarkMode ? (theme.text.titleLight?.toString() ?? '#40a098') : (lightTheme.text?.titleLight?.toString() ?? '#43a49e');
  const airportBorderColor = isAirportDarkMode ? (theme.borderColorLight?.toString() ?? '#5F7486') : (lightTheme.borderColorLight?.toString() ?? '#e9e9e9');
  const airportFieldBackground = isAirportDarkMode
    ? (theme.background.primaryLight?.toString() ?? '#344450')
    : (lightTheme.background?.primary?.toString() ?? '#ffffff');
  const airportPlaceholderTextColor = isAirportDarkMode
    ? (theme.inputTextPlaceholder?.toString() ?? '#989898')
    : (lightTheme.inputTextPlaceholder?.toString() ?? '#989898');
  const t = useTranslation();
  const referAFriendEnabled = useReferAFriendEnabled();

  const continueAfterGeolocationNotice = (requirement: GeolocationRequirement | undefined, radius: number | undefined, onContinue: () => void) => {
    if (requirement == null || requirement === GeolocationRequirement.No) {
      onContinue();
      return;
    }

    const handleContinue = () => {
      dispatch(lightBoxActions.setNoDismiss(false));
      dispatch(lightBoxActions.setVisibility(false));
      dispatch(lightBoxActions.setContent(<></>));
      onContinue();
    };

    dispatch(lightBoxActions.setNoDismiss(true));
    dispatch(
      lightBoxActions.setContent(
        <GeolocationAcceptanceModal requirement={requirement} radius={radius} onContinue={handleContinue} />,
      ),
    );
    dispatch(lightBoxActions.setIsLoading(false));
    dispatch(lightBoxActions.setVisibility(true));
  };
  const dispatch = useAppDispatch();
  const userPrefs = useAppSelector((selector) => selector.userPrefs);
  const styles = useStyles((sector) => sector.missionDetailCard);
  const stylesCard = useStyles((section) => section.missionCard);
  const sharedButtonStyles = useStyles((section) => section.customButton);
  const authStore = useAppSelector((selector) => selector.auth);
  const apiMyMission = useApi(MyMissionClient);
  const apiMyMissionAirport = useApi(MyMissionClient);
  const apiMissions = useApi(MissionsClient);
  const apiMysteryShopper = useApi(MysteryShopperClient);
  const advertisementTracking = useAdvertisementTrackingHook();
  const getAirportOptionLabel = (option: AirportDestinationOption) => {
    if (option.code === 'CA-OTHER') {
      return t('airport-booking-elsewhere-canada');
    }

    if (option.code === 'INT-OTHER') {
      return t('airport-booking-other-international');
    }

    return option.label;
  };

  const formatReimbursementPopupValue = useCallback(
    (value: number) => {
      const safeValue = Number.isFinite(value) ? value : 0;

      try {
        const locale = userPrefs.uiLanguage === 'fr' ? 'fr-CA' : 'en-CA';
        return new Intl.NumberFormat(locale, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(safeValue);
      } catch {
        return safeValue.toFixed(2);
      }
    },
    [userPrefs.uiLanguage],
  );

  const formatReimbursementPercentageValue = useCallback(
    (value: number) => {
      const safeValue = Number.isFinite(value) ? value : 0;

      try {
        const locale = userPrefs.uiLanguage === 'fr' ? 'fr-CA' : 'en-CA';
        return new Intl.NumberFormat(locale, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(safeValue);
      } catch {
        return `${safeValue}`;
      }
    },
    [userPrefs.uiLanguage],
  );

  const formatCurrencyValue = useCallback(
    (value?: number | string) => {
      const numericValue =
        typeof value === 'number'
          ? value
          : Number(
              String(value ?? '0')
                .replace(/\s/g, '')
                .replace(',', '.'),
            );
      const safeValue = Number.isFinite(numericValue) ? numericValue : 0;
      const isFrench = userPrefs.uiLanguage?.startsWith('fr');

      try {
        const formattedValue = new Intl.NumberFormat(isFrench ? 'fr-CA' : 'en-CA', {
          maximumFractionDigits: 2,
        }).format(safeValue);

        return isFrench ? `${formattedValue}\u00a0$` : `$${formattedValue}`;
      } catch {
        return isFrench ? `${safeValue}\u00a0$` : `$${safeValue}`;
      }
    },
    [userPrefs.uiLanguage],
  );

  const defaultLogo = useMemo(() => <FontAwesomeIcon icon={faStore} style={stylesCard.header.logo.icon} />, [stylesCard]);
  const airportDefaultLogo = useMemo(
    () => <FontAwesomeIcon icon={faStore} style={stylesCard.header.logo.icon} color={airportHeaderTitleColor} />,
    [stylesCard, airportHeaderTitleColor],
  );
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [missionFromMap, setMissionFromMap] = useState<GetMissionsFromMapDto>();
  const [location, SetLocation] = useState<MapLocation>();
  const [missionDetails, setMissionDetails] = useState<MissionDetailItem[]>();
  const [acceptMissionViewModels, setAcceptMissionViewModels] = useState<GetAcceptMissionByBranchDto[]>([]);
  const [canAcceptMissionQuerySeed, setCanAcceptMissionQuerySeed] = useState<string>(StringHelper.generateRandomString());
  const [recheckCanAcceptMission, setRecheckCanAcceptMission] = useState<string>(undefined);
  const [acceptMissionQueue, setAcceptMissionQueue] = useState<GetMissionDetailsDto[]>([]);
  const acceptMissionQueueRef = useRef<GetMissionDetailsDto[]>([]);
  const [expandedMissionDetailAccordion, setExpandedMissionDetailAccordion] = useState<
    { missionId: string; section: MissionBranchDetailAccordionSection } | undefined
  >(undefined);
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  const [showAirportBookingPopup, setShowAirportBookingPopup] = useState(false);
  const [referMissionPopup, setReferMissionPopup] = useState<MissionReferPopupState>();
  const [airportBookingMissionId, setAirportBookingMissionId] = useState<string>();
  const [airportDepartureDate, setAirportDepartureDate] = useState('');
  const [airportDepartureTime, setAirportDepartureTime] = useState('');
  const [airportDestination, setAirportDestination] = useState('');
  const [airportHasConnection, setAirportHasConnection] = useState(false);
  const [airportConnectionDestination, setAirportConnectionDestination] = useState('');
  const [airportDestinationCountryCode, setAirportDestinationCountryCode] = useState<string | undefined>();
  const [showAirportConnectionDestinationDropdown, setShowAirportConnectionDestinationDropdown] = useState(false);
  const [selectedAirportConnectionDestination, setSelectedAirportConnectionDestination] = useState<AirportDestinationOption | undefined>();
  const [isResolvingAirportDestinationCountry, setIsResolvingAirportDestinationCountry] = useState(false);
  const [airportBookingError, setAirportBookingError] = useState<string | undefined>();
  const airportMissionLimitRef = useRef<number | undefined>(undefined);
  const airportMissionTakenCountRef = useRef<number | undefined>(undefined);
  const airportDepartureMinDate = moment().startOf('day');
  const airportDepartureMaxDate = moment().add(12, 'months').startOf('day');
  const drawerMoreInfoButtonProps = {
    suspendDrawerOnOpen: false as const,
    useLightBoxOnNative: true as const,
  };

  const [showAnonymousPopup, setShowAnonymousPopup] = useState(<></>);

  const isReferralMission = useCallback(
    (periodMissionConfigurationId?: string, branchId?: string) =>
      !!props.missionReferralToken &&
      !!props.referralPeriodMissionConfigurationId &&
      props.referralPeriodMissionConfigurationId === periodMissionConfigurationId &&
      missionFromMap?.branchId === branchId,
    [missionFromMap?.branchId, props.missionReferralToken, props.referralPeriodMissionConfigurationId],
  );

  const toggleMissionDetailAccordion = useCallback((missionId: string, section: MissionBranchDetailAccordionSection) => {
    setExpandedMissionDetailAccordion((currentSection) =>
      currentSection?.missionId === missionId && currentSection.section === section ? undefined : { missionId, section },
    );
  }, []);

  const isAirportMissionDetail = useCallback(
    (mission?: GetMissionDetailsDto): boolean => {
      const effectiveMissionType = mission?.missionType ?? missionFromMap?.missionType ?? MissionType.Regular;
      return effectiveMissionType === MissionType.Airport;
    },
    [missionFromMap?.missionType],
  );

  const normalizeAirportMissionLimitState = useCallback((result?: GetAirportTakenCountViewModel) => {
    const normalizedLimit = Number(result?.limit);
    if (Number.isFinite(normalizedLimit) && normalizedLimit > 0) {
      airportMissionLimitRef.current = normalizedLimit;
    }

    const normalizedTakenCount = Number(result?.takenCount);
    if (Number.isFinite(normalizedTakenCount) && normalizedTakenCount >= 0) {
      airportMissionTakenCountRef.current = normalizedTakenCount;
    }

    const resolvedLimit = Number.isFinite(normalizedLimit) && normalizedLimit > 0 ? normalizedLimit : airportMissionLimitRef.current;
    const resolvedTakenCount = Number.isFinite(normalizedTakenCount) && normalizedTakenCount >= 0 ? normalizedTakenCount : airportMissionTakenCountRef.current;
    const remainingMissions = resolvedLimit !== undefined && resolvedTakenCount !== undefined ? Math.max(0, resolvedLimit - resolvedTakenCount) : undefined;

    return {
      reachedLimit: !!result?.reachedLimit,
      limit: resolvedLimit,
      takenCount: resolvedTakenCount,
      remainingMissions,
    };
  }, []);

  const getAirportLimitText = useCallback(
    (key: string) => {
      const baseText = t(key);
      const limitValue = airportMissionLimitRef.current;

      if (!limitValue) {
        return baseText;
      }

      const withTemplateValue = t(key, { limit: limitValue, count: limitValue });
      if (withTemplateValue && withTemplateValue !== key && withTemplateValue !== baseText) {
        return withTemplateValue;
      }

      return baseText.replace(/\d+/, `${limitValue}`);
    },
    [t],
  );

  const getAirportRemainingMissionsText = useCallback(
    (key: string) => {
      const baseText = t(key);
      const limitValue = airportMissionLimitRef.current;
      const takenCountValue = airportMissionTakenCountRef.current;

      if (limitValue === undefined || takenCountValue === undefined) {
        return baseText;
      }

      const remainingValue = Math.max(0, limitValue - takenCountValue);
      const withTemplateValue = t(key, { remaining: remainingValue, count: remainingValue });
      if (withTemplateValue && withTemplateValue !== key && withTemplateValue !== baseText) {
        return withTemplateValue;
      }

      return baseText.replace(/\d+/, `${remainingValue}`);
    },
    [t],
  );

  const { refetch: refetchAirportTakenCount } = useQuery<GetAirportTakenCountViewModel | undefined>({
    enabled: false,
    queryKey: ['airportTakenCountQuery', authStore.idToken],
    queryFn: ({ signal }) => {
      signal?.addEventListener('abort', () => {
        apiMyMissionAirport.tokenSource?.cancel('Query was cancelled by React Query');
        apiMyMissionAirport.onCancel();
      });

      return apiMyMissionAirport.getAirportTakenCount(apiMyMissionAirport.tokenSource?.token);
    },
    onSuccess: (result) => {
      normalizeAirportMissionLimitState(result);
    },
  });

  const syncAirportMissionCounter = useCallback(async () => {
    if (!authStore.idToken) {
      return undefined;
    }

    const result = await refetchAirportTakenCount();
    return normalizeAirportMissionLimitState(result.data);
  }, [authStore.idToken, refetchAirportTakenCount, normalizeAirportMissionLimitState]);

  const configureCenteredLightBoxOverlay = () => {
    dispatch(lightBoxActions.setOverlayVisible(true));
    dispatch(lightBoxActions.setAllowBackgroundInteraction(false));
    dispatch(lightBoxActions.setContentVerticalAlign('center'));
    dispatch(lightBoxActions.setContentHorizontalAlign('center'));
    dispatch(lightBoxActions.setContentTopOffset(0));
    dispatch(lightBoxActions.setContentBottomOffset(0));
    dispatch(lightBoxActions.setContentLeftOffset(0));
  };

  const showSecondLightBoxFromMissionDrawer = (content: ReactElement) => {
    dispatch(lightBoxActions.setSecondContent(content));
    dispatch(lightBoxActions.setSecondVisibility(true));
  };

  const renderAirportFollowUpPopup = ({
    imageSource,
    title,
    body,
    cta,
    onPress,
  }: {
    imageSource: any;
    title: string;
    body: string;
    cta: string;
    onPress: () => void;
  }) => (
    <View style={{ width: 380, maxWidth: '100%', overflow: 'hidden', backgroundColor: airportFollowUpPopupBackground, alignSelf: 'center' }}>
      <View style={{ width: '100%', height: 260, backgroundColor: airportFollowUpPopupBackground }}>
        <Image source={imageSource} style={{ width: '100%', height: '100%' }} resizeMode="cover" />
      </View>
      <Pressable onPress={() => dispatch(lightBoxActions.setVisibility(false))} hitSlop={12} style={{ position: 'absolute', top: 10, right: 10, zIndex: 2 }}>
        <Label style={{ fontSize: 40, color: '#ffffff', lineHeight: 36 }}>×</Label>
      </Pressable>
      <View style={{ paddingHorizontal: 36, paddingBottom: 26, paddingTop: 16, alignItems: 'center', backgroundColor: airportFollowUpPopupBackground }}>
        <Label style={{ fontSize: 20, lineHeight: 24, color: airportTitleColor, fontWeight: '700', textAlign: 'center' }}>{title}</Label>
        <Label style={{ fontSize: 15, color: airportBodyColor, marginTop: 10, textAlign: 'center' }}>{body}</Label>
        <Pressable
          onPress={onPress}
          style={[sharedButtonStyles.button.main, { marginTop: 18, width: '100%', backgroundColor: airportTitleColor, borderColor: airportTitleColor }]}
        >
          <ImageBackground
            source={require('../../../../assets/disabledBars.png')}
            style={[
              sharedButtonStyles.button.backgroundImage,
              {
                flex: 0,
                height: undefined,
                minHeight: 44,
              },
            ]}
            imageStyle={{ resizeMode: 'repeat', opacity: 0 }}
          >
            <Text
              style={[
                sharedButtonStyles.text.main,
                {
                  color: '#ffffff',
                  width: '100%',
                  flexShrink: 1,
                  paddingHorizontal: 16,
                },
              ]}
            >
              {`${cta}`}
            </Text>
          </ImageBackground>
        </Pressable>
      </View>
    </View>
  );

  const closeAirportPopupAndReturnToAirportMissionList = () => {
    dispatch(lightBoxActions.setVisibility(false));
    dispatch(drawerActions.setState(DrawerOpenState.closed));
    dispatch(drawerActions.resetState());
    dispatch(missionsActions.setSelectedBranch({ id: undefined, lat: undefined, lng: undefined }));
    handleOnBackPress();
  };

  const closeAirportPopupAndExploreRegularMissions = () => {
    dispatch(lightBoxActions.setVisibility(false));
    dispatch(drawerActions.setState(DrawerOpenState.closed));
    dispatch(drawerActions.resetState());
    dispatch(missionsActions.setSelectedBranch({ id: undefined, lat: undefined, lng: undefined }));
    if (props.onExploreOtherMissionsPress) {
      props.onExploreOtherMissionsPress();
      return;
    }
    handleOnBackPress();
  };

  const showAirportMoreMissionsPopup = () => {
    configureCenteredLightBoxOverlay();
    dispatch(
      lightBoxActions.setContent(
        renderAirportFollowUpPopup({
          imageSource: require('../../../../assets/airport-more-mission.png'),
          title: t('airport-more-popup-title'),
          body: getAirportRemainingMissionsText('airport-more-popup-subtitle'),
          cta: t('airport-more-popup-cta'),
          onPress: closeAirportPopupAndReturnToAirportMissionList,
        }),
      ),
    );
    dispatch(lightBoxActions.setVisibility(true));
  };

  const showAirportReachedMissionsLimitPopup = () => {
    configureCenteredLightBoxOverlay();
    dispatch(
      lightBoxActions.setContent(
        renderAirportFollowUpPopup({
          imageSource: require('../../../../assets/pilot-thumbs-up.png'),
          title: t('airport-limit-reached-popup-title'),
          body: getAirportLimitText('airport-limit-reached-popup-subtitle'),
          cta: t('airport-limit-reached-popup-cta'),
          onPress: closeAirportPopupAndReturnToAirportMissionList,
        }),
      ),
    );
    dispatch(lightBoxActions.setVisibility(true));
  };

  const showAirportNoMoreMissionsPopup = () => {
    configureCenteredLightBoxOverlay();
    dispatch(
      lightBoxActions.setContent(
        renderAirportFollowUpPopup({
          imageSource: require('../../../../assets/airport-no-more-mission.png'),
          title: t('airport-max-popup-title'),
          body: getAirportLimitText('airport-max-popup-subtitle'),
          cta: t('airport-max-popup-cta'),
          onPress: closeAirportPopupAndExploreRegularMissions,
        }),
      ),
    );
    dispatch(lightBoxActions.setVisibility(true));
  };

  useEffect(() => {
    setMissionFromMap(props.missionFromMap);
  }, [props.missionFromMap]);

  useEffect(() => {
    setMissionDetails(undefined);
    setExpandedMissionDetailAccordion(undefined);
    setCanAcceptMissionQuerySeed(StringHelper.generateRandomString());
    setRecheckCanAcceptMission(undefined);
  }, [props.missionFromMap?.branchId]);

  useEffect(() => {
    SetLocation(props.location);
  }, [props.location]);

  const airportDetailSearchContext = missionFromMap?.missionType === MissionType.Airport ? props.airportSearchContext : undefined;
  const airportDetailSearchDateTimeKey = airportDetailSearchContext?.searchDateTime;
  const airportDetailSearchDateTime = useMemo(() => {
    const searchDateTime = airportDetailSearchContext?.searchDateTime?.trim();
    if (!searchDateTime) {
      return undefined;
    }

    const parsedDateTime = moment(searchDateTime, ['YYYY-MM-DDTHH:mm:ssZ', 'YYYY-MM-DDTHH:mm:ss'], true);
    if (!parsedDateTime.isValid()) {
      return undefined;
    }

    const localDateTime = parsedDateTime.toDate();

    // The regenerated client still serializes this field via Date.toISOString().
    // Shadow it so the backend receives the original wall-clock value instead.
    Object.defineProperty(localDateTime, 'toISOString', {
      value: () => searchDateTime,
      configurable: true,
    });

    return localDateTime;
  }, [airportDetailSearchContext?.searchDateTime]);

  const branchDetailQuery = useQuery({
    enabled: !!missionFromMap?.branchId && !!location,
    queryKey: [
      'branchDetailQuery',
      missionFromMap?.branchId,
      location?.lat,
      location?.lng,
      userPrefs.uiLanguage,
      airportDetailSearchDateTimeKey,
      airportDetailSearchContext?.onlyAcceptableMissions,
    ],
    queryFn: () => {
      return apiMissions.getMissionDetails(
        missionFromMap?.branchId,
        `${location.lat}&${location.lng}`,
        airportDetailSearchContext?.onlyAcceptableMissions ?? false,
        airportDetailSearchDateTime,
      );
    },
  });

  useEffect(() => {
    if (branchDetailQuery.isSuccess) {
      const nextMissionDetails = branchDetailQuery.data?.missionDetails ?? [];
      const missionCount = nextMissionDetails.length;
      const orderedMissionDetails = props.referralPeriodMissionConfigurationId
        ? [...nextMissionDetails].sort((left, right) => {
            const leftIsReferral = left.periodMissionConfigurationId === props.referralPeriodMissionConfigurationId;
            const rightIsReferral = right.periodMissionConfigurationId === props.referralPeriodMissionConfigurationId;
            return Number(rightIsReferral) - Number(leftIsReferral);
          })
        : nextMissionDetails;
      setMissionDetails(
        orderedMissionDetails.map((x) => {
          return {
            dto: x,
            expanded:
              x.periodMissionConfigurationId === props.referralPeriodMissionConfigurationId || shouldExpandMissionByDefault(missionCount, x),
          };
        }),
      );
    }
  }, [branchDetailQuery.isSuccess, branchDetailQuery.data, props.referralPeriodMissionConfigurationId]);

  const hasAirportMissionDetails = useMemo(() => missionDetails?.some((item) => isAirportMissionDetail(item.dto)) ?? false, [missionDetails, missionFromMap]);
  const isAirportDrawerFlow = missionFromMap?.missionType === MissionType.Airport || hasAirportMissionDetails;

  useEffect(() => {
    if (!hasAirportMissionDetails) {
      return;
    }

    void syncAirportMissionCounter();
  }, [hasAirportMissionDetails, syncAirportMissionCounter]);

  const handleOnBackPress = () => {
    if (props.onBackPress) {
      props.onBackPress();
    }
  };

  const referralAcceptabilityQuery = useQuery({
    enabled:
      !!props.missionReferralToken &&
      !!props.referralPeriodMissionConfigurationId &&
      !!missionFromMap?.branchId &&
      !!authStore.idToken,
    retry: false,
    queryKey: [
      'missionReferralAcceptability',
      props.missionReferralToken,
      props.referralPeriodMissionConfigurationId,
      canAcceptMissionQuerySeed,
    ],
    queryFn: () => apiMissions.getMissionReferralAcceptability(props.missionReferralToken!, apiMissions.tokenSource?.token),
  });

  const mergeReferralAcceptability = useCallback(
    (items?: GetAcceptMissionByBranchDto[]) => {
      const currentItems = items ?? [];
      const acceptability = referralAcceptabilityQuery.data;

      if (!acceptability || !props.referralPeriodMissionConfigurationId) {
        return currentItems;
      }

      const referralMissionStatus = GetAcceptMissionByBranchDto.fromJS({
        periodMissionConfigurationId: props.referralPeriodMissionConfigurationId,
        acceptMissionStatus: acceptability.acceptMissionStatus,
        missionAssignationId: acceptability.missionAssignationId,
        rotationInfo: acceptability.rotationInfo,
        isReferable: false,
        ageRestrictionType: acceptability.ageRestrictionType,
        requiresRelatedMinorSelection: acceptability.requiresRelatedMinorSelection,
        eligibleRelatedMinors: acceptability.eligibleRelatedMinors,
        missingProfileMembers: acceptability.missingProfileMembers,
      });

      return [
        referralMissionStatus,
        ...currentItems.filter((item) => item.periodMissionConfigurationId !== props.referralPeriodMissionConfigurationId),
      ];
    },
    [props.referralPeriodMissionConfigurationId, referralAcceptabilityQuery.data],
  );

  const canAcceptMissionQuery = useQuery({
    enabled: !!missionFromMap?.branchId && !!authStore.idToken,
    queryKey: ['canAcceptMissionQuery', missionFromMap?.branchId, canAcceptMissionQuerySeed],
    queryFn: () => {
      return apiMissions.getAcceptMissions(missionFromMap?.branchId, apiMissions.tokenSource?.token);
    },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    onSuccess(data) {
      setAcceptMissionViewModels(mergeReferralAcceptability(data.items));
    },
    onError: (err) => {
      setRecheckCanAcceptMission(undefined);
      setLoading(false);
      throw err;
    },
  });

  useEffect(() => {
    if (referralAcceptabilityQuery.data) {
      setAcceptMissionViewModels((currentItems) => mergeReferralAcceptability(currentItems));
    }
  }, [mergeReferralAcceptability, referralAcceptabilityQuery.data]);

  useEffect(() => {
    setAcceptMissionViewModels(undefined);
  }, [missionFromMap?.branchId]);

  const acceptMission = useMutation({
    retry: false,
    mutationFn: async (missionInfo: { periodMissionConfigurationId: string; branchId: string; startDate?: Date; selectedRelatedMinorId?: string }) => {
      setAcceptLoading(true);

      if (isReferralMission(missionInfo.periodMissionConfigurationId, missionInfo.branchId)) {
        const result = await apiMissions.acceptMissionReferral(
          props.missionReferralToken!,
          AcceptMissionReferralRequest.fromJS({
            startDate: missionInfo.startDate,
            selectedRelatedMinorId: missionInfo.selectedRelatedMinorId,
          }),
          apiMissions.tokenSource?.token,
        );
        let acceptMissionStatus = result.acceptMissionStatus;

        if (result.status === AcceptMissionReferralStatus.InvitationAccepted) {
          acceptMissionStatus = AcceptMissionStatus.MissionAlreadyInProgress;
        } else if (
          result.status === AcceptMissionReferralStatus.InvitationCanceled ||
          result.status === AcceptMissionReferralStatus.Expired
        ) {
          acceptMissionStatus = AcceptMissionStatus.MissionUnavailable;
        } else if (result.status === AcceptMissionReferralStatus.Retry) {
          acceptMissionStatus = AcceptMissionStatus.Retry;
        }

        return AcceptMissionViewModel.fromJS({
          success: result.success,
          missionEndDate: result.missionEndDate,
          acceptMissionStatus,
          hardLimitReached: result.hardLimitReached,
          verifyUserPhoneNumber: result.verifyUserPhoneNumber,
          missionAssignationId: result.missionAssignationId,
          geolocationRequirement: result.geolocationRequirement,
          geolocationRadiusMeters: result.geolocationRadiusMeters,
          rotationInfo: result.rotationInfo,
          ageRestrictionType: referralAcceptabilityQuery.data?.ageRestrictionType,
          requiresRelatedMinorSelection: result.requiresRelatedMinor,
          eligibleRelatedMinors: result.eligibleRelatedMinors,
          missingProfileMembers: result.missingProfileMembers,
        });
      }

      return apiMissions.acceptMission(
        missionInfo.periodMissionConfigurationId,
        missionInfo.branchId,
        missionInfo.startDate,
        missionInfo.selectedRelatedMinorId,
        apiMyMission.tokenSource?.token,
      );
    },
    onError: (err) => {
      setAcceptMissionQueue([]);
      setRecheckCanAcceptMission(undefined);
      dispatch(lightBoxActions.setVisibility(false));
      dispatch(lightBoxActions.setIsLoading(false));
      setAcceptLoading(false);
      setLoading(false);
      throw err;
    },
  });

  const triggerAcceptMission = useCallback(
    (periodMissionConfigurationId: string, branchId?: string, selectedRelatedMinorId?: string) => {
      if (!branchId) {
        return;
      }

      const selectedMission = missionDetails?.find((x) => x.dto.periodMissionConfigurationId === periodMissionConfigurationId)?.dto;
      const shouldPassAirportStartDate = isAirportMissionDetail(selectedMission);

      acceptMission.mutate({
        branchId,
        periodMissionConfigurationId,
        startDate: shouldPassAirportStartDate ? props.airportStartDate : undefined,
        selectedRelatedMinorId,
      });
    },
    [acceptMission.mutate, isAirportMissionDetail, missionDetails, props.airportStartDate],
  );

  useEffect(() => {
    if (acceptMission.isSuccess) {
      setRecheckCanAcceptMission(undefined);

      if (acceptMission.data.hardLimitReached) {
        const hardLimitMission = missionDetails?.find((x) => x.dto.periodMissionConfigurationId === acceptMission.variables.periodMissionConfigurationId)?.dto;
        const isAirportHardLimit = isAirportMissionDetail(hardLimitMission);
        dispatch(lightBoxActions.setIsLoading(false));
        if (isAirportHardLimit) {
          handleOnBackPress();
          setTimeout(() => {
            showAirportNoMoreMissionsPopup();
          }, 0);
        } else {
          dispatch(
            lightBoxActions.setContent(
              <View style={{ flexGrow: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 16, paddingVertical: 16 }}>
                <Label style={{ fontSize: 16, fontWeight: '600' }}>{t('HardLimitReached')}</Label>
              </View>,
            ),
          );
          dispatch(lightBoxActions.setVisibility(true));
        }
      } else if (acceptMission.data.verifyUserPhoneNumber) {
        dispatch(lightBoxActions.setIsLoading(false));
        dispatch(lightBoxActions.setVisibility(false));
        setShowVerificationPopup(true);
        setAcceptLoading(false);
      } else {
        const nextQueue = acceptMissionQueueRef.current.filter((x) => x.periodMissionConfigurationId !== acceptMission.variables.periodMissionConfigurationId);
        acceptMissionQueueRef.current = nextQueue;
        setAcceptMissionQueue(nextQueue);

        if (nextQueue.length === 0) {
          dispatch(lightBoxActions.setVisibility(false));
          dispatch(lightBoxActions.setIsLoading(false));
          setAcceptLoading(false);
          setLoading(false);

          if (acceptMission?.data?.success) {
            const acceptedMission = missionDetails?.find(
              (x) => x.dto.periodMissionConfigurationId === acceptMission.variables.periodMissionConfigurationId,
            )?.dto;
            const isAirportAcceptedMission = isAirportMissionDetail(acceptedMission);
            const acceptedMissionAssignationId = acceptMission.data.missionAssignationId;

            dispatch(drawerActions.resetState());
            dispatch(missionsActions.setMissionAcceptedId(acceptedMissionAssignationId));
            dispatch(missionsActions.setReloadMissionListSeed(StringHelper.generateRandomString(5)));
            dispatch(missionsActions.setSelectedBranch({ id: undefined, lat: undefined, lng: undefined }));
            if (isReferralMission(acceptMission.variables.periodMissionConfigurationId, acceptMission.variables.branchId)) {
              props.onMissionReferralConsumed?.();
            }
            acceptMission.reset();

            continueAfterGeolocationNotice(
              acceptMission.data.geolocationRequirement,
              acceptMission.data.geolocationRadiusMeters,
              () => {
                if (isAirportAcceptedMission) {
                  handleOnBackPress();
                  const openAirportPopupFromLatestState = async () => {
                    const latestLimitState = await syncAirportMissionCounter();
                    if (!!latestLimitState?.reachedLimit || (latestLimitState?.remainingMissions ?? 0) <= 0) {
                      showAirportReachedMissionsLimitPopup();
                      return;
                    }

                    showAirportMoreMissionsPopup();
                  };

                  setTimeout(() => {
                    void openAirportPopupFromLatestState();
                  }, 0);
                } else if (props.onAcceptedMission) {
                  props.onAcceptedMission(acceptedMissionAssignationId);
                } else {
                  handleOnBackPress();
                  rootNavigate('mymissions', acceptedMissionAssignationId ? { id: acceptedMissionAssignationId } : undefined);
                }
              },
            );
          } else {
            setAcceptMissionViewModels((prev) => {
              const missionStatus = prev?.find((x) => x.periodMissionConfigurationId === acceptMission.variables.periodMissionConfigurationId);
              if (!missionStatus) {
                return prev;
              }

              missionStatus.missingProfileMembers = acceptMission.data.missingProfileMembers;
              missionStatus.acceptMissionStatus = acceptMission.data.acceptMissionStatus;
              missionStatus.ageRestrictionType = acceptMission.data.ageRestrictionType;
              missionStatus.rotationInfo = acceptMission.data.rotationInfo;
              missionStatus.requiresRelatedMinorSelection = acceptMission.data.requiresRelatedMinorSelection;
              missionStatus.eligibleRelatedMinors = acceptMission.data.eligibleRelatedMinors;

              return prev.slice();
            });

            if (
              acceptMission.data.acceptMissionStatus === AcceptMissionStatus.MissionEligible &&
              acceptMission.data.requiresRelatedMinorSelection &&
              acceptMission.data.eligibleRelatedMinors?.length
            ) {
              showRelatedMinorSelectionPopup(
                {
                  periodMissionConfigurationId: acceptMission.variables.periodMissionConfigurationId,
                  acceptMissionStatus: acceptMission.data.acceptMissionStatus,
                  ageRestrictionType: acceptMission.data.ageRestrictionType,
                  requiresRelatedMinorSelection: acceptMission.data.requiresRelatedMinorSelection,
                  eligibleRelatedMinors: acceptMission.data.eligibleRelatedMinors,
                } as GetAcceptMissionByBranchDto,
                acceptMission.variables.branchId,
              );
            }

            acceptMission.reset();
          }
        } else {
          triggerAcceptMission(nextQueue[0].periodMissionConfigurationId, missionFromMap.branchId);
        }
      }
    }
  }, [acceptMission.isSuccess, acceptMission.data, missionDetails, missionFromMap?.branchId, triggerAcceptMission]);

  useEffect(() => {
    acceptMissionQueueRef.current = acceptMissionQueue;
  }, [acceptMissionQueue]);

  const runWithProfileManager = (callback: (manager: ProfileManagerHandles) => void, remainingAttempts: number = 6) => {
    const manager = profileManager.current;
    if (manager) {
      callback(manager);
      return;
    }

    if (remainingAttempts <= 0) {
      return;
    }

    setTimeout(() => {
      runWithProfileManager(callback, remainingAttempts - 1);
    }, 50);
  };

  const handleOnConfirm = (dto: GetMysteryShopperProfileMemberDto, onConfirm: any) => {
    runWithProfileManager((manager) => {
      manager.showProfileMemberPopup(
        {
          value: '',
          profileMember: dto,
        },
        () => {
          onConfirm();
        },
        { suspendDrawer: false },
      );
    });
  };

  const handleOnExplanationClose = (dontShowAgain) => {
    if (dontShowAgain) {
      advertisementTracking.dontShowAgain('ProfileRequirement');
    }
  };

  useEffect(() => {
    if (!recheckCanAcceptMission) {
      return;
    }

    if (!missionFromMap?.branchId) {
      setRecheckCanAcceptMission(undefined);
      return;
    }

    if (acceptLoading || acceptMission.isPending) {
      return;
    }

    if (!acceptMissionViewModels) {
      return;
    }

    if (acceptMissionViewModels.length === 0) {
      setRecheckCanAcceptMission(undefined);
      return;
    }

    const pendingMissionConfigurationId = recheckCanAcceptMission;
    setRecheckCanAcceptMission(undefined);

    // Consume the pending retry before invoking accept so later query refreshes can't replay it.
    handleAcceptMission(pendingMissionConfigurationId, missionFromMap.branchId);
  }, [recheckCanAcceptMission, acceptMissionViewModels, missionFromMap?.branchId, acceptLoading, acceptMission.isPending]);

  const verifyHaveUserBasicInfo = (userPrefs: UserPreferencesState, periodMissionConfigurationId) => {
    runWithProfileManager((manager) => {
      if (
        manager.verifyUserCanAcceptMission(
          userPrefs,
          (pref) => {
            verifyHaveUserBasicInfo(pref, periodMissionConfigurationId);
          },
          { suspendDrawer: false },
        )
      ) {
        setAcceptMissionViewModels(undefined);
        setCanAcceptMissionQuerySeed(StringHelper.generateRandomString());
        setRecheckCanAcceptMission(periodMissionConfigurationId);
      }
    });
  };

  const needToKnowMoreAboutUserPopup = (periodMissionConfigurationId) => {
    if (
      authStore.logged !== LoggedState.Anonymous &&
      (userPrefs.firstName === '' ||
        userPrefs.firstName === undefined ||
        userPrefs.lastName === '' ||
        userPrefs.lastName === undefined ||
        userPrefs.phoneNumber === undefined ||
        userPrefs.phoneNumber === '' ||
        userPrefs.profiles?.find((x) => x.key === 'BirthDate') === undefined ||
        userPrefs.profiles?.find((x) => x.key === 'Gender') === undefined ||
        userPrefs.profiles?.find((x) => x.key === 'EducationLevel') === undefined)
    ) {
      showSecondLightBoxFromMissionDrawer(
        <ModalContainer
          buttonText={t('im-ready')}
          onConfirm={() => {
            verifyHaveUserBasicInfo(userPrefs, periodMissionConfigurationId);
            dispatch(lightBoxActions.setSecondVisibility(false));
          }}
          onClose={() => {
            dispatch(lightBoxActions.setSecondVisibility(false));
          }}
        >
          <View style={{ flexShrink: 1, alignItems: 'center' }}>
            <Image style={{ width: 116, height: 68, tintColor: theme.isDark ? '#ffffff' : undefined }} source={require('../../../../assets/annoucement.png')} />
            <Label style={{ fontSize: 26, fontWeight: '600', color: theme.text.sea, textAlign: 'center' }}>{t('Attention')}</Label>
            <Label style={{ fontSize: 18, fontWeight: '600', color: theme.text.darkTurquoiseTwo, textAlign: 'center' }}>
              {t('We-need-some-basic-information-before-getting-star')}
            </Label>
            <Label style={{ marginTop: 12, fontSize: 14, textAlign: 'center' }}>{t('Please-complete-your-profile-by-answering-the-foll')}</Label>
          </View>
        </ModalContainer>,
      );
    } else {
      // if (!authStore.multifactor.signInSecondFactor) {
      //   //sharedPopup.showAccountVerificationPopup();
      // } else {
      handleAcceptMission(periodMissionConfigurationId, missionFromMap.branchId);
      // }
    }
  };

  const showExplanationPopup = (dto: GetMysteryShopperProfileMemberDto, onConfirm: any) => {
    dispatch(
      lightBoxActions.setContent(
        <ModalContainer buttonText={t('im-ready')} onConfirm={() => handleOnConfirm(dto, onConfirm)} onClose={handleOnExplanationClose}>
          <View style={{ flexShrink: 1, alignItems: 'center' }}>
            <Image style={{ width: 116, height: 68, tintColor: theme.isDark ? '#ffffff' : undefined }} source={require('../../../../assets/annoucement.png')} />
            <Label style={{ fontSize: 26, fontWeight: '600', color: theme.text.sea, textAlign: 'center' }}>{t('Attention')}</Label>
            <Label style={{ fontSize: 18, fontWeight: '600', color: theme.text.darkTurquoiseTwo, textAlign: 'center' }}>
              {t('Some-missions-require-a-specific-profile')}
            </Label>
            <Label style={{ marginTop: 12, fontSize: 14, textAlign: 'center' }}>{t('Please-complete-your-profile-by-answering-the-foll')}</Label>
          </View>
        </ModalContainer>,
      ),
    );
    dispatch(lightBoxActions.setVisibility(true));
  };

  const showRelatedMinorSelectionPopup = (data: GetAcceptMissionByBranchDto, branchId: string) => {
    dispatch(lightBoxActions.setKeepDrawerVisible(true));
    dispatch(
      lightBoxActions.setContent(
        <RelatedMinorSelectionPopup
          minors={data.eligibleRelatedMinors}
          onConfirm={(selectedRelatedMinorId) => {
            dispatch(lightBoxActions.setVisibility(false));
            triggerAcceptMission(data.periodMissionConfigurationId, branchId, selectedRelatedMinorId);
          }}
        />,
      ),
    );
    dispatch(lightBoxActions.setVisibility(true));
  };

  const closeMissionReferPopup = () => {
    setReferMissionPopup(undefined);
  };

  const openMissionReferPopup = (missionDetail: GetMissionDetailsDto) => {
    setReferMissionPopup({
      branchId: missionFromMap?.branchId,
      periodMissionConfigurationId: missionDetail.periodMissionConfigurationId,
    });
  };

  const handleAcceptMission = (periodMissionConfigurationId, branchId) => {
    let data = acceptMissionViewModels.find((x) => x.periodMissionConfigurationId === periodMissionConfigurationId);
    if (data?.acceptMissionStatus === AcceptMissionStatus.MissionEligible) {
      // if (missionDetails?.filter((x) => x.dto.acceptMissionStatus === AcceptMissionStatus.MissionEligible).length > 1) {
      //   showComboPopup(periodMissionConfigurationId);
      // } else {
      triggerAcceptMission(periodMissionConfigurationId, branchId);
      //}
    } else {
      setLoading(false);

      if (authStore.logged === LoggedState.Anonymous) {
        configureCenteredLightBoxOverlay();
        dispatch(
          lightBoxActions.setContent(
            <LoginPopup
              onLogin={() => {
                dispatch(lightBoxActions.setVisibility(false));
                rootNavigate('logout');
              }}
              onSignUp={() => {
                dispatch(lightBoxActions.setVisibility(false));
                rootNavigate('logout', { signUp: true });
              }}
            />,
          ),
        );
        dispatch(lightBoxActions.setVisibility(true));
      } else {
        setShowAnonymousPopup(<></>);

        if (data.acceptMissionStatus === AcceptMissionStatus.IncompleteProfileRequirements) {
          if (data.missingProfileMembers[0]) {
            if (advertisementTracking.showAdvertisement('ProfileRequirement')) {
              showExplanationPopup(data.missingProfileMembers[0], () => {
                triggerAcceptMission(periodMissionConfigurationId, branchId);
              });
            } else {
              runWithProfileManager((manager) => {
                manager.showProfileMemberPopup(
                  {
                    value: '',
                    profileMember: data.missingProfileMembers[0],
                  },
                  () => {
                    triggerAcceptMission(periodMissionConfigurationId, branchId);
                  },
                  { suspendDrawer: false },
                );
              });
            }
          }
        }

        if (data.acceptMissionStatus === AcceptMissionStatus.UserIneligibleForMission) {
          dispatch(
            lightBoxActions.setContent(
              <View style={{ flexGrow: 1, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 16 }}>
                <Label style={{ fontSize: 16, fontWeight: '600' }}>{missionStatusText(data)}</Label>
                {shouldShowIdVerificationCta(data) && (
                  <>
                    <CustomButton
                      title={t('id-verification')}
                      onPress={openIdVerificationPanel}
                      style={{ marginTop: 16, alignSelf: 'center', width: 220, maxWidth: '100%' }}
                    />
                    <Pressable accessibilityRole="link" onPress={openIdVerificationHelp} style={{ marginTop: 12, alignSelf: 'center', padding: 4 }}>
                      <Label style={{ color: lightTheme.text?.darkTurquoiseTwo, fontSize: 16, lineHeight: 20 }}>{t('learn-more')}</Label>
                    </Pressable>
                  </>
                )}
              </View>,
            ),
          );
          dispatch(lightBoxActions.setVisibility(true));
        }

        if (data.acceptMissionStatus === AcceptMissionStatus.MissionAlreadyInProgress) {
          dispatch(
            lightBoxActions.setContent(
              <View style={{ flexGrow: 1, alignItems: 'center', justifyContent: 'center' }}>
                <Label style={{ fontSize: 16, fontWeight: '600' }}>{t('MissionAlreadyInProgress')}</Label>
              </View>,
            ),
          );
          dispatch(lightBoxActions.setVisibility(true));
        }

        if (data.acceptMissionStatus === AcceptMissionStatus.MissionUnavailable) {
          dispatch(
            lightBoxActions.setContent(
              <View style={{ flexGrow: 1, alignItems: 'center', justifyContent: 'center' }}>
                <Label style={{ fontSize: 16, fontWeight: '600' }}>{t('MissionUnavailable')}</Label>
              </View>,
            ),
          );
          dispatch(lightBoxActions.setVisibility(true));
        }

        if (data.acceptMissionStatus === AcceptMissionStatus.MissionLimitReached) {
          dispatch(
            lightBoxActions.setContent(
              <View style={{ flexGrow: 1, alignItems: 'center', justifyContent: 'center' }}>
                <Label style={{ fontSize: 16, fontWeight: '600' }}>{t('MissionLimitReached')}</Label>
              </View>,
            ),
          );
          dispatch(lightBoxActions.setVisibility(true));
        }

        if (data.acceptMissionStatus === AcceptMissionStatus.UserNotAuthenticated) {
          dispatch(
            lightBoxActions.setContent(
              <View style={{ flexGrow: 1, alignItems: 'center', justifyContent: 'center' }}>
                <Label style={{ fontSize: 16, fontWeight: '600' }}>{t('UserNotAuthenticated')}</Label>
              </View>,
            ),
          );
          dispatch(lightBoxActions.setVisibility(true));
        }
      }
    }
  };

  const maybeShowAirportNotificationPrompt = async () => {
    try {
      const notificationSettings = await apiMysteryShopper.getNotificationSettings(apiMysteryShopper.tokenSource?.token);
      const pushEnabled = notificationSettings?.notificationSettings?.some((x) => x.pushNotificationEnabled);

      if (!pushEnabled) {
        showSecondLightBoxFromMissionDrawer(
          <NotificationPermission
            onClose={() => {
              dispatch(lightBoxActions.setSecondVisibility(false));
            }}
          />,
        );
      }
    } catch {
      // no-op: booking flow should proceed even if notification setting check fails
    }
  };

  const openAirportBookingPopup = (periodMissionConfigurationId: string) => {
    setAirportBookingMissionId(periodMissionConfigurationId);
    setAirportDepartureDate('');
    setAirportDepartureTime('');
    setAirportDestination('');
    setAirportHasConnection(false);
    setAirportConnectionDestination('');
    setAirportDestinationCountryCode(undefined);
    setShowAirportConnectionDestinationDropdown(false);
    setSelectedAirportConnectionDestination(undefined);
    setIsResolvingAirportDestinationCountry(false);
    setAirportBookingError(undefined);
    setShowAirportBookingPopup(true);
  };

  const resolveDestinationCountryCode = async (destination: string): Promise<string | undefined> => {
    try {
      const key = (Constants.expoConfig as any)?.extra?.googleApiKey ?? (Constants.expoConfig as any)?.android?.config?.googleMaps?.apiKey;

      if (!key || !destination?.trim()) {
        return undefined;
      }

      const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(destination.trim())}&key=${encodeURIComponent(key)}`;
      const res = await fetch(url);
      if (!res.ok) return undefined;

      const payload = await res.json();
      const firstResult = payload?.results?.[0];
      const countryComponent = firstResult?.address_components?.find((x: any) => Array.isArray(x?.types) && x.types.includes('country'));

      const code = (countryComponent?.short_name ?? countryComponent?.long_name ?? '').toString().trim().toUpperCase();
      return code || undefined;
    } catch {
      return undefined;
    }
  };

  const isCanadaCountry = (countryCodeOrName?: string) => {
    const normalized = (countryCodeOrName ?? '').trim().toUpperCase();
    return normalized === 'CA' || normalized === 'CANADA';
  };

  const confirmAirportBooking = async () => {
    if (!airportDepartureDate.trim() || !airportDepartureTime.trim() || !airportDestination.trim()) {
      setAirportBookingError(t('airport-booking-error-provide-details'));
      return;
    }

    if (airportHasConnection && !selectedAirportConnectionDestination) {
      setAirportBookingError(t('airport-booking-error-connection-required'));
      return;
    }

    setAirportBookingError(undefined);
    setIsResolvingAirportDestinationCountry(true);
    const resolvedCountryCode = await resolveDestinationCountryCode(airportDestination);
    setIsResolvingAirportDestinationCountry(false);
    setAirportDestinationCountryCode(resolvedCountryCode);

    const missionDtos = missionDetails?.map((x) => x.dto) ?? [];

    const selectedMission = missionDtos.find((x) => x.periodMissionConfigurationId === airportBookingMissionId);
    const eligibleMission = selectedMission ?? missionDtos.find((x) => isAirportMissionDetail(x)) ?? missionDtos[0];

    if (!eligibleMission?.periodMissionConfigurationId) {
      setAirportBookingError(t('airport-booking-error-no-mission-for-route'));
      return;
    }

    setShowAirportBookingPopup(false);
    void maybeShowAirportNotificationPrompt();
    needToKnowMoreAboutUserPopup(eligibleMission.periodMissionConfigurationId);
  };

  const buttonIsDisabled = (status?: AcceptMissionStatus) => {
    return (
      authStore.logged !== LoggedState.Anonymous && status != AcceptMissionStatus.MissionEligible && status != AcceptMissionStatus.IncompleteProfileRequirements
    );
  };

  const showComboPopup = (periodMissionConfigurationId: string) => {
    dispatch(
      lightBoxActions.setContent(
        <ComboPopup
          onClose={() => {
            dispatch(lightBoxActions.setOnClose(undefined));
            dispatch(lightBoxActions.setVisibility(false));
          }}
          onConfirm={(missions) => {
            dispatch(lightBoxActions.setIsLoading(true));
            if (missions.length > 0) {
              setAcceptMissionQueue(missions);
              triggerAcceptMission(missions[0].periodMissionConfigurationId, missionFromMap.branchId);
            }
          }}
          missions={missionDetails
            .filter((x) => x.dto.acceptMissionStatus === AcceptMissionStatus.MissionEligible)
            .map((x) => {
              let item: ComboMissionItem = {
                dto: x.dto,
                selected: x.dto.periodMissionConfigurationId === periodMissionConfigurationId,
                branchName: missionFromMap?.name,
              };
              return item;
            })}
        />,
      ),
    );
    dispatch(
      lightBoxActions.setOnClose(() => {
        dispatch(lightBoxActions.setOnClose(undefined));
      }),
    );
    dispatch(lightBoxActions.setVisibility(true));
  };

  const missionStatusText = (missionStatus?: AcceptMissionStatus | GetAcceptMissionByBranchDto | false) => {
    if (!missionStatus) {
      return undefined;
    }

    const status = typeof missionStatus === 'object' ? missionStatus.acceptMissionStatus : missionStatus;

    if (
      typeof missionStatus === 'object' &&
      status === AcceptMissionStatus.UserIneligibleForMission &&
      missionStatus.ageRestrictionType === AgeRestrictionType.Minor &&
      missionStatus.requiresRelatedMinorSelection &&
      !missionStatus.eligibleRelatedMinors?.length
    ) {
      return t('RelatedMinorRequiredForMission');
    }

    if (
      typeof missionStatus === 'object' &&
      status === AcceptMissionStatus.UserIneligibleForMission &&
      missionStatus.ageRestrictionType === AgeRestrictionType.Adult
    ) {
      return t('AgeRestrictionRequiredForMission');
    }

    if (typeof missionStatus === 'object' && isLinkedMysteryShopperRotationLimitReached(missionStatus.rotationInfo)) {
      return t('linked-mystery-shopper-rotation-limit-reached');
    }

    switch (status) {
      case AcceptMissionStatus.MissionLimitReached:
        return t('MissionLimitReached');
      case AcceptMissionStatus.MissionAlreadyInProgress:
        return t('MissionAlreadyInProgress');
      case AcceptMissionStatus.MissionLimitRotationReach:
        return t('MissionLimitRotationReach');
      case AcceptMissionStatus.MissionUnavailable:
        return t('MissionUnavailable');
      case AcceptMissionStatus.UserIneligibleForMission:
        return t('UserIneligibleForMission');
      case AcceptMissionStatus.UserNotAuthenticated:
        return t('UserNotAuthenticated');
      case AcceptMissionStatus.Retry:
        return t('AcceptMissionRetry');
    }

    return undefined;
  };

  const shouldShowIdVerificationCta = (missionStatus?: GetAcceptMissionByBranchDto | false) => {
    return (
      !!missionStatus &&
      missionStatus.acceptMissionStatus === AcceptMissionStatus.UserIneligibleForMission &&
      (missionStatus.ageRestrictionType === AgeRestrictionType.Adult ||
        (missionStatus.ageRestrictionType === AgeRestrictionType.Minor &&
          !!missionStatus.requiresRelatedMinorSelection &&
          !missionStatus.eligibleRelatedMinors?.length))
    );
  };

  const openIdVerificationPanel = () => {
    dispatch(lightBoxActions.setVisibility(false));
    dispatch(lightBoxActions.setSecondVisibility(false));
    dispatch(myAccountActions.setReturnDestination('mission'));
    dispatch(myAccountActions.navigate(NavigationElements.idVerification));
    dispatch(myAccountActions.setVisibility(true));
  };

  const openIdVerificationHelp = () => {
    Linking.openURL(t('id-verification-lanla-website-support-link')).catch(() => undefined);
  };

  const openMapsWithDirections = (name, address) => {
    const scheme = Platform.OS === 'ios' ? 'maps://app?daddr=' : Platform.OS === 'web' ? 'https://www.google.com/maps/search/?api=1&query=' : 'geo:0,0?q=';
    const query = `${name},${address}`;
    const url = Platform.OS === 'ios' ? `${scheme}${query}` : `${scheme}${query}`;

    Linking.openURL(url);
  };

  const isAirportBranchHeader = missionFromMap?.missionType === MissionType.Airport;
  const headerAirportInfo =
    missionFromMap?.branchAirportInfo ?? missionDetails?.find((missionDetail) => missionDetail.dto.branchAirportInfo)?.dto.branchAirportInfo;
  const headerAddress = missionFromMap?.address ? (
    <Label variant={LabelType.body1} style={isAirportBranchHeader ? { color: airportHeaderTextColor } : undefined}>
      {missionFromMap.address}
    </Label>
  ) : null;
  const headerLogo =
    missionFromMap?.logoUrl && missionFromMap?.hasLogo ? (
      <Image style={stylesCard.header.logo.image} source={{ uri: missionFromMap.logoUrl }} />
    ) : isAirportBranchHeader ? (
      airportDefaultLogo
    ) : (
      defaultLogo
    );

  return (
    <ScrollView
      style={[styles.missionsBranchDetail.container, isAirportDrawerFlow ? { backgroundColor: airportDrawerBackground } : undefined]}
      nestedScrollEnabled={true}
      keyboardShouldPersistTaps="handled"
    >
      <Modal animationType="none" visible={showVerificationPopup} transparent={true}>
        <Overlay showOverLay={true} zIndex={3000}>
          <Popup suspendDrawer={false}>
            <AccountVerificationPopup
              onClose={() => {
                setShowVerificationPopup(false);
              }}
            />
          </Popup>
        </Overlay>
      </Modal>
      <Modal animationType="none" visible={showAirportBookingPopup} transparent={true}>
        <Overlay showOverLay={true} zIndex={3000}>
          <Popup>
            <View style={{ padding: 16, minWidth: 330, maxWidth: 380, gap: 10, backgroundColor: airportPopupBackground }}>
              <Label style={{ fontSize: 20, fontWeight: '700', color: airportHeaderTitleColor }}>{t('airport-booking-title')}</Label>
              <Label style={{ fontSize: 14, color: airportHeaderTextColor, marginTop: 4 }}>{t('airport-booking-when-leaving')}</Label>
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <View style={{ flex: 1 }}>
                  <Label style={{ fontSize: 13, color: airportHeaderTextColor, marginBottom: 4 }}>{t('airport-booking-date')}</Label>
                  <CustomDatePicker
                    selectedDate={airportDepartureDate}
                    placeholder={t('airport-booking-date-placeholder')}
                    minDate={airportDepartureMinDate.toDate()}
                    maxDate={airportDepartureMaxDate.toDate()}
                    onChangeText={(value) => setAirportDepartureDate(value)}
                    fieldStyle={{ backgroundColor: airportFieldBackground, borderColor: airportBorderColor }}
                    valueTextColor={airportHeaderTextColor}
                    placeholderTextColor={airportPlaceholderTextColor}
                    iconColor={airportTitleColor}
                  />
                </View>
                <View style={{ flex: 1 }}>
                  <Label style={{ fontSize: 13, color: airportHeaderTextColor, marginBottom: 4 }}>{t('airport-booking-time')}</Label>
                  <CustomTimePicker
                    selectedTime={airportDepartureTime}
                    placeholder={t('airport-booking-time-placeholder')}
                    onChangeText={(value) => setAirportDepartureTime(value)}
                    fieldStyle={{ backgroundColor: airportFieldBackground, borderColor: airportBorderColor }}
                    valueTextColor={airportHeaderTextColor}
                    placeholderTextColor={airportPlaceholderTextColor}
                    iconColor={airportTitleColor}
                  />
                </View>
              </View>
              <Label style={{ fontSize: 14, color: airportHeaderTextColor }}>{t('airport-booking-destination')}</Label>
              <CustomTextInput
                value={airportDestination}
                onChangeText={(value) => setAirportDestination(value)}
                placeholder={t('airport-booking-destination-placeholder')}
                style={{ backgroundColor: airportFieldBackground, borderColor: airportBorderColor, color: airportHeaderTextColor }}
                forcePlaceholderTextColor={airportPlaceholderTextColor}
              />
              <Label style={{ fontSize: 14, color: airportHeaderTextColor }}>{t('airport-booking-connecting-flight')}</Label>
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <Pressable
                  onPress={() => setAirportHasConnection(false)}
                  style={{
                    paddingVertical: 8,
                    paddingHorizontal: 14,
                    borderRadius: 16,
                    backgroundColor: !airportHasConnection ? airportTitleColor : airportFieldBackground,
                    borderWidth: 1,
                    borderColor: airportTitleColor,
                  }}
                >
                  <Label style={{ color: !airportHasConnection ? '#ffffff' : airportHeaderTextColor, fontWeight: '600' }}>{t('no')}</Label>
                </Pressable>
                <Pressable
                  onPress={() => setAirportHasConnection(true)}
                  style={{
                    paddingVertical: 8,
                    paddingHorizontal: 14,
                    borderRadius: 16,
                    backgroundColor: airportHasConnection ? airportTitleColor : airportFieldBackground,
                    borderWidth: 1,
                    borderColor: airportTitleColor,
                  }}
                >
                  <Label style={{ color: airportHasConnection ? '#ffffff' : airportHeaderTextColor, fontWeight: '600' }}>{t('yes')}</Label>
                </Pressable>
              </View>
              {airportHasConnection && (
                <>
                  <Label style={{ fontSize: 14, color: airportHeaderTextColor }}>{t('airport-booking-connection-destination')}</Label>
                  <Pressable
                    onPress={() => setShowAirportConnectionDestinationDropdown((prev) => !prev)}
                    style={{
                      borderWidth: 1,
                      borderColor: airportBorderColor,
                      borderRadius: 8,
                      paddingHorizontal: 10,
                      paddingVertical: 10,
                      backgroundColor: airportFieldBackground,
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Label style={{ color: selectedAirportConnectionDestination ? airportHeaderTextColor : airportPlaceholderTextColor }}>
                      {selectedAirportConnectionDestination
                        ? `${selectedAirportConnectionDestination.code} - ${getAirportOptionLabel(selectedAirportConnectionDestination)}`
                        : t('airport-booking-select-airport')}
                    </Label>
                    <FontAwesomeIcon icon={showAirportConnectionDestinationDropdown ? faChevronUp : faChevronDown} color={airportTitleColor} />
                  </Pressable>
                  {showAirportConnectionDestinationDropdown && (
                    <View
                      style={{
                        borderWidth: 1,
                        borderColor: airportBorderColor,
                        borderRadius: 8,
                        maxHeight: 220,
                        backgroundColor: airportFieldBackground,
                        paddingVertical: 8,
                      }}
                    >
                      <ScrollView>
                        <Label style={{ fontWeight: '700', marginHorizontal: 10, marginBottom: 6, color: airportHeaderTitleColor }}>
                          {t('airport-route-ca')}
                        </Label>
                        {canadaAirportOptions.map((option) => (
                          <Pressable
                            key={`ca-${option.code}`}
                            onPress={() => {
                              setSelectedAirportConnectionDestination(option);
                              setAirportConnectionDestination(`${option.code} - ${getAirportOptionLabel(option)}`);
                              setShowAirportConnectionDestinationDropdown(false);
                            }}
                            style={{ paddingHorizontal: 10, paddingVertical: 8 }}
                          >
                            <Label style={{ color: airportHeaderTextColor }}>{`${option.code} - ${getAirportOptionLabel(option)}`}</Label>
                          </Pressable>
                        ))}
                        <Label style={{ fontWeight: '700', marginHorizontal: 10, marginTop: 8, marginBottom: 6, color: airportHeaderTitleColor }}>
                          {t('airport-booking-others')}
                        </Label>
                        {otherAirportOptions.map((option) => (
                          <Pressable
                            key={`int-${option.code}`}
                            onPress={() => {
                              setSelectedAirportConnectionDestination(option);
                              setAirportConnectionDestination(`${option.code} - ${getAirportOptionLabel(option)}`);
                              setShowAirportConnectionDestinationDropdown(false);
                            }}
                            style={{ paddingHorizontal: 10, paddingVertical: 8 }}
                          >
                            <Label style={{ color: airportHeaderTextColor }}>{`${option.code} - ${getAirportOptionLabel(option)}`}</Label>
                          </Pressable>
                        ))}
                      </ScrollView>
                    </View>
                  )}
                </>
              )}
              {isResolvingAirportDestinationCountry && <Label style={{ color: airportHeaderTextColor }}>{t('airport-booking-checking-country')}</Label>}
              {!isResolvingAirportDestinationCountry && airportDestinationCountryCode && (
                <Label style={{ color: airportHeaderTextColor }}>
                  {t('airport-booking-destination-country', {
                    country: airportDestinationCountryCode,
                    wing: isCanadaCountry(airportDestinationCountryCode) ? t('airport-booking-wing-domestic') : t('airport-booking-wing-international'),
                  })}
                </Label>
              )}
              {!!airportBookingError && <Label style={{ color: lightTheme.text?.error?.toString() ?? '#8e183d' }}>{airportBookingError}</Label>}
              <View style={{ marginTop: 8, flexDirection: 'row', justifyContent: 'space-between', gap: 8 }}>
                <CustomButton
                  title={t('cancel')}
                  style={{ flex: 1, backgroundColor: airportFieldBackground, borderColor: airportTitleColor }}
                  textStyle={{ color: airportTitleColor }}
                  onPress={() => {
                    setShowAirportBookingPopup(false);
                  }}
                />
                <CustomButton
                  title={t('confirm')}
                  style={{ flex: 1, backgroundColor: airportTitleColor, borderColor: airportTitleColor }}
                  textStyle={{ color: '#ffffff' }}
                  onPress={confirmAirportBooking}
                />
              </View>
            </View>
          </Popup>
        </Overlay>
      </Modal>
      <Modal animationType="none" visible={!!referMissionPopup} transparent={true} onRequestClose={closeMissionReferPopup}>
        <Overlay showOverLay={!!referMissionPopup} zIndex={3000} onPress={closeMissionReferPopup}>
          <MissionReferPopup
            periodMissionConfigurationId={referMissionPopup?.periodMissionConfigurationId}
            branchId={referMissionPopup?.branchId}
            onClose={closeMissionReferPopup}
          />
        </Overlay>
      </Modal>
      {Platform.OS === 'web' && (
        <View style={[styles.missionsBranchDetail.backButton, isAirportDrawerFlow ? { backgroundColor: airportDrawerBackground } : undefined]}>
          <NavigationButton
            icon={faCircleChevronLeft}
            iconSize={24}
            color={isAirportDrawerFlow ? airportBackButtonColor : theme.text.titleLight}
            labelVariant={LabelType.titleSmall}
            onPress={handleOnBackPress}
            text={t('back')}
          />
        </View>
      )}
      {!loading && (
        <>
          {missionFromMap && (
            <View style={[styles.missionsBranchDetail.content.body, isAirportDrawerFlow ? { backgroundColor: airportDrawerBackground } : undefined]}>
              {showAnonymousPopup}
              <View style={stylesCard.cardBase}>
                <View
                  style={[
                    stylesCard.header.logoBase,
                    isAirportDrawerFlow ? { backgroundColor: airportFieldBackground, borderColor: airportBorderColor } : undefined,
                  ]}
                >
                  {headerLogo}
                </View>
                <View style={stylesCard.info}>
                  <View style={stylesCard.missionName}>
                    <Label
                      variant={LabelType.title}
                      style={isAirportDrawerFlow ? { color: airportHeaderTitleColor } : undefined}
                    >{`${missionFromMap?.name}`}</Label>
                    {missionFromMap.isHidden && <FontAwesomeIcon icon={faEyeSlash} style={stylesCard.missionHiddenIcon} />}
                  </View>
                  {headerAddress}
                  <MissionAirportLocationInfo
                    branchAirportInfo={headerAirportInfo}
                    containerStyle={{ marginTop: 2, marginBottom: 2 }}
                    textStyle={isAirportBranchHeader ? { color: airportHeaderTextColor } : undefined}
                    textVariant={LabelType.body1}
                  />
                  <Pressable
                    style={{
                      flexDirection: 'row',
                      gap: 4,
                      alignItems: 'center',
                      borderRadius: 20,
                    }}
                    onPress={() => {
                      openMapsWithDirections(missionFromMap.name, missionFromMap.address);
                    }}
                  >
                    <FontAwesomeIcon icon={faMap} color={isAirportDrawerFlow ? airportHeaderLinkColor : theme.text.darkTurquoiseTwo.toString()} size={16} />
                    <Label style={{ fontSize: 11, color: isAirportDrawerFlow ? airportHeaderLinkColor : theme.text.darkTurquoiseTwo, fontWeight: '500' }}>
                      {t('Get-directions')}
                    </Label>
                  </Pressable>
                  <>
                    {!!missionFromMap.phone && (
                      <Label style={{ fontSize: 11, color: isAirportDrawerFlow ? airportHeaderLinkColor : theme.text.darkTurquoiseTwo, fontWeight: '500' }}>
                        {missionFromMap.phone}
                      </Label>
                    )}
                  </>
                  <View style={stylesCard.preTitle}>
                    <Label variant={LabelType.caption} style={isAirportDrawerFlow ? { color: airportHeaderTextColor } : undefined}>
                      {missionFromMap?.tags === '' ? 'N/A' : missionFromMap?.tags}
                    </Label>
                    <Label style={{ flexShrink: 0, ...(isAirportDrawerFlow ? { color: airportHeaderTextColor } : {}) }} variant={LabelType.caption}>
                      {`${(missionFromMap.distance / 1000.0).toFixed(2)}km`}
                    </Label>
                  </View>

                  {/* <Pressable
                    style={{ flexDirection: 'row', gap: 4, alignItems: 'center' }}
                    onPress={() => {
                      openMapsWithDirections(missionFromMap.name, missionFromMap.address);
                    }}
                  >
                    <FontAwesomeIcon icon={faMap} size={16} />
                    <Label style={{ flexShrink: 1 }}>{address}</Label>
                  </Pressable> */}
                </View>

                <MoreInfoButton {...drawerMoreInfoButtonProps} title={t('help_support')} color={isAirportDrawerFlow ? airportHeaderLinkColor : undefined}>
                  {`${t('help.make-sure-you-have-selected-the-correct-branch')}`}
                </MoreInfoButton>
              </View>
              {false && (
                <View style={styles.missionsBranchDetail.content.missionsCountBase}>
                  <View style={styles.missionsBranchDetail.content.missionsCount.icons}>
                    <Image source={pinSmallBlue} style={styles.missionsBranchDetail.content.missionsCount.icon} />
                    <Image source={pinSmallGreen} style={styles.missionsBranchDetail.content.missionsCount.icon} />
                    <Image source={pinSmallOrange} style={styles.missionsBranchDetail.content.missionsCount.icon} />
                  </View>
                  <Label variant={LabelType.caption} style={styles.missionsBranchDetail.content.missionsCount.label}>
                    {missionDetails?.length ?? 0} {t('missions-for-this-point-of-sale')}
                  </Label>
                </View>
              )}
              {missionDetails ? (
                <>
                  {missionDetails.map((missionDetail) => {
                    const missionType = missionDetail.dto.missionType ?? missionFromMap?.missionType ?? MissionType.Regular;
                    const isAirportMission = missionType === MissionType.Airport;
                    const isFlashMission = !!missionDetail.dto.isFlashMission;
                    const titleTextColor = isFlashMission ? flashTitleColor : isAirportMission ? airportTitleColor : undefined;
                    const bodyTextColor = isFlashMission ? flashBodyColor : isAirportMission ? airportBodyColor : undefined;
                    const summaryIconColor = bodyTextColor ?? theme.text.primary.toString();
                    const titleIconColor = titleTextColor ?? theme.text.titleDark.toString();
                    const linkTextColor = isFlashMission ? flashErrorColor : isAirportMission ? airportErrorColor : theme.text.error;
                    const isDarkTheme = !!theme.isDark;
                    const languageWarningColor = isFlashMission
                      ? flashErrorColor
                      : isAirportMission
                        ? (lightTheme.text?.error?.toString() ?? '#8e183d')
                        : isDarkTheme
                          ? '#ffccd9'
                          : theme.text.error;
                    const missionBackgroundStyle = isFlashMission
                      ? {
                          ...styles.missionsBranchDetail.content.infoContainerBase.flashMission,
                          overflow: 'hidden',
                        }
                      : isAirportMission
                        ? { backgroundColor: airportCardBackground, overflow: 'hidden' }
                        : {};
                    const missionSummaryContainerStyle = isFlashMission
                      ? { backgroundColor: flashSummaryBackground }
                      : isAirportMission
                        ? { backgroundColor: airportHeaderBackground }
                        : undefined;
                    const missionSummaryStyle = isFlashMission
                      ? styles.missionsBranchDetail.content.infoContainer.summaryItem.flashMission
                      : isAirportMission
                        ? { backgroundColor: airportHeaderBackground }
                        : {};
                    const missionTitleStyle = isAirportMission
                      ? { backgroundColor: airportHeaderBackground }
                      : isFlashMission
                        ? { backgroundColor: flashDetailBackground }
                        : undefined;
                    const missionDetailAccordionId = missionDetail.dto.periodMissionConfigurationId ?? '';
                    const acceptMissionViewModel = acceptMissionViewModels?.find(
                      (x) => x.periodMissionConfigurationId === missionDetail.dto.periodMissionConfigurationId,
                    );
                    const shouldShowAcceptMissionButton = !buttonIsDisabled(acceptMissionViewModel?.acceptMissionStatus);
                    const shouldShowReferMissionButton = referAFriendEnabled && acceptMissionViewModel?.isReferable === true;
                    const missionIsAvailableForDiscovery = isMissionAvailableForDiscovery(
                      missionDetail.dto,
                      !buttonIsDisabled(missionDetail.dto.acceptMissionStatus),
                    );
                    const missionAvailabilityIcon = missionIsAvailableForDiscovery
                      ? isFlashMission
                        ? boltIcon
                        : isAirportMission
                          ? pinSmallBlue
                          : pinSmallOrange
                      : isFlashMission
                        ? boltIconBw
                        : pinSmallGrey;
                    const eligibilityText = missionDetail.dto.details.find((x) => x.type === MissionConfigurationDetailType.Eligibility)?.text ?? '';
                    const termsAndConditionsText =
                      missionDetail.dto.details.find((x) => x.type === MissionConfigurationDetailType.TermsAndConditions)?.text ?? '';
                    const missionDetailMarkdownStyle = bodyTextColor ? { body: { color: bodyTextColor, lineHeight: 16 } } : undefined;
                    const renderMissionDetailAccordion = (section: MissionBranchDetailAccordionSection, title: string, icon: IconProp, content: string) => {
                      const isExpanded =
                        expandedMissionDetailAccordion?.missionId === missionDetailAccordionId && expandedMissionDetailAccordion.section === section;

                      return (
                        <View style={styles.missionsBranchDetail.content.infoContainer.infoBlocks.accordion.base}>
                          <Pressable
                            accessibilityRole="button"
                            accessibilityState={{ expanded: isExpanded }}
                            style={styles.missionsBranchDetail.content.infoContainer.infoBlocks.accordion.header}
                            onPress={() => toggleMissionDetailAccordion(missionDetailAccordionId, section)}
                          >
                            <FontAwesomeIcon
                              icon={icon}
                              style={styles.missionsBranchDetail.content.infoContainer.infoBlocks.titleIcon}
                              color={titleIconColor}
                            />
                            <Label
                              variant={LabelType.titleDark}
                              style={[
                                styles.missionsBranchDetail.content.infoContainer.infoBlocks.accordion.title,
                                titleTextColor ? { color: titleTextColor } : undefined,
                              ]}
                              numberOfLines={3}
                            >
                              {title}
                            </Label>
                            <FontAwesomeIcon
                              icon={isExpanded ? faChevronUp : faChevronDown}
                              size={16}
                              style={styles.missionsBranchDetail.content.infoContainer.infoBlocks.accordion.chevron}
                              color={titleIconColor}
                            />
                          </Pressable>
                          {isExpanded && (
                            <View style={styles.missionsBranchDetail.content.infoContainer.infoBlocks.accordion.content}>
                              <CustomMarkdown style={missionDetailMarkdownStyle}>{content}</CustomMarkdown>
                            </View>
                          )}
                        </View>
                      );
                    };
                    const reimbursementMaxAmount = Number(missionDetail.dto.reimbursementMaxAmount ?? 0);
                    const reimbursementValueRaw = missionDetail.dto.reimbursementValue ?? '';
                    const isDollarReimbursement = reimbursementValueRaw.includes('$');
                    const reimbursementValueNumeric = (() => {
                      const normalized = reimbursementValueRaw.replace(/\s/g, '').replace(/,/g, '.');
                      const match = normalized.match(/(\d+(?:\.\d+)?)/);
                      if (!match) {
                        return 0;
                      }

                      const parsed = Number(match[1]);
                      return Number.isFinite(parsed) ? parsed : 0;
                    })();
                    const reimbursementPercentageRate = !isDollarReimbursement && reimbursementValueNumeric > 0 ? reimbursementValueNumeric / 100 : 0;
                    const hasValidPercentageReimbursement = reimbursementPercentageRate > 0;
                    const totalValue = Math.max(0, reimbursementMaxAmount > 0 ? reimbursementMaxAmount : reimbursementValueNumeric);
                    const exceedingValue = totalValue + 5;
                    const underValue = totalValue > 15 ? totalValue - 15 : totalValue / 2;
                    const pointsRateDollarsNewKey = 'help.points-rate-dollars-new';
                    const dynamicPointsRateDollarsText = t(pointsRateDollarsNewKey, {
                      totalValue: formatReimbursementPopupValue(totalValue),
                      exceedingValue: formatReimbursementPopupValue(exceedingValue),
                      underValue: formatReimbursementPopupValue(underValue),
                    });
                    const pointsRateDollarsText =
                      dynamicPointsRateDollarsText === pointsRateDollarsNewKey ? t('help.points-rate-dollars') : dynamicPointsRateDollarsText;
                    const maxPercentageReimbursementAmount = reimbursementMaxAmount > 0 ? reimbursementMaxAmount : Number.POSITIVE_INFINITY;
                    const purchaseAmountToReachMaxReimbursement =
                      reimbursementMaxAmount > 0 && hasValidPercentageReimbursement ? reimbursementMaxAmount / reimbursementPercentageRate : 0;
                    const lowerPercentagePurchaseAmount = hasValidPercentageReimbursement
                      ? normalizeReimbursementExampleAmount(reimbursementMaxAmount > 0 ? Math.min(100, purchaseAmountToReachMaxReimbursement / 2) : 100, 'down')
                      : 0;
                    const higherPercentagePurchaseAmount = hasValidPercentageReimbursement
                      ? normalizeReimbursementExampleAmount(
                          reimbursementMaxAmount > 0 ? Math.max(purchaseAmountToReachMaxReimbursement * 1.2, lowerPercentagePurchaseAmount + 25) : 200,
                          'up',
                        )
                      : 0;
                    const lowerPercentageReimbursementAmount = hasValidPercentageReimbursement
                      ? Math.min(maxPercentageReimbursementAmount, lowerPercentagePurchaseAmount * reimbursementPercentageRate)
                      : 0;
                    const higherPercentageReimbursementAmount = hasValidPercentageReimbursement
                      ? Math.min(maxPercentageReimbursementAmount, higherPercentagePurchaseAmount * reimbursementPercentageRate)
                      : 0;
                    const pointsRatePercentageNewKey =
                      reimbursementMaxAmount > 0 ? 'help.points-rate-percentage-new' : 'help.points-rate-percentage-no-max-new';
                    const dynamicPointsRatePercentageText = hasValidPercentageReimbursement
                      ? t(pointsRatePercentageNewKey, {
                          percentageValue: formatReimbursementPercentageValue(reimbursementValueNumeric),
                          maxAmount: formatReimbursementPopupValue(reimbursementMaxAmount),
                          lowerPurchaseAmount: formatReimbursementPopupValue(lowerPercentagePurchaseAmount),
                          lowerReimbursementAmount: formatReimbursementPopupValue(lowerPercentageReimbursementAmount),
                          higherPurchaseAmount: formatReimbursementPopupValue(higherPercentagePurchaseAmount),
                          higherReimbursementAmount: formatReimbursementPopupValue(higherPercentageReimbursementAmount),
                        })
                      : '';
                    const pointsRatePercentageText =
                      !hasValidPercentageReimbursement || dynamicPointsRatePercentageText === pointsRatePercentageNewKey
                        ? t('help.points-rate-percentage')
                        : dynamicPointsRatePercentageText;
                    const shouldShowReimbursementMoreInfo = !isDollarReimbursement || totalValue > 0;
                    return (
                      <View
                        key={missionDetail.dto.periodMissionConfigurationId}
                        style={[
                          styles.missionsBranchDetail.content.infoContainerBase,
                          { paddingBottom: missionDetail.expanded ? 12 : 0 },
                          missionBackgroundStyle,
                        ]}
                      >
                        <Pressable
                          style={[styles.missionsBranchDetail.content.infoContainer.title, missionTitleStyle]}
                          onPress={(e) => {
                            setMissionDetails((prev) => {
                              let detail = prev.find((x) => x.dto.periodMissionConfigurationId === missionDetail.dto.periodMissionConfigurationId);
                              detail.expanded = !detail.expanded;
                              return prev.slice();
                            });
                          }}
                        >
                          <Image
                            source={missionAvailabilityIcon}
                            style={
                              isFlashMission
                                ? styles.missionsBranchDetail.content.infoContainer.iconBolt
                                : styles.missionsBranchDetail.content.infoContainer.icon
                            }
                          />
                          <Label
                            style={{ fontSize: 15, fontWeight: '700', flexShrink: 1, flexGrow: 1, ...(titleTextColor ? { color: titleTextColor } : {}) }}
                            numberOfLines={1}
                          >
                            {missionDetail.dto.missionConfigurationName}
                            {/* <FontAwesomeIcon icon={faCheck} color={theme.text.titleDark.toString()} style={{ marginLeft: 8 }} /> */}
                          </Label>
                          {missionDetail.dto.isTaken && (
                            <Pressable
                              style={{ flexDirection: 'row', alignItems: 'center', marginRight: 4, flexShrink: 0 }}
                              onPress={() => {
                                dispatch(drawerActions.resetState());
                                rootNavigate('mymissions', { id: missionDetail.dto.takenMissionAssignationId });
                              }}
                            >
                              <FontAwesomeIcon icon={faCheck} color={titleIconColor} size={12} />
                              <Label variant={LabelType.caption} style={{ ...StylesHelper.padding(4, 4, 4, 2), color: titleIconColor }}>
                                {t('Assigned')}
                              </Label>
                            </Pressable>
                          )}
                          <View style={{ width: 18, height: 18, marginLeft: 8, alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <FontAwesomeIcon
                              icon={missionDetail.expanded ? faChevronUp : faChevronDown}
                              size={14}
                              color={isAirportMission ? airportTitleColor : titleIconColor}
                            />
                          </View>
                        </Pressable>
                        {missionDetail.expanded && (
                          <>
                            {missionDetail.dto.supportedCultureCodes?.length > 0 && (
                              <View style={{ flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingBottom: 16 }}>
                                <Label style={{ fontSize: 11, color: languageWarningColor, fontWeight: '500' }}>
                                  {t('Mission-in')} {missionDetail.dto.supportedCultureCodes?.map((x) => t(x)).join(', ')} {t('only')}!
                                </Label>
                                <MoreInfoButton
                                  {...drawerMoreInfoButtonProps}
                                  style={{ marginLeft: 8 }}
                                  title={''}
                                  color={bodyTextColor ?? theme.text.primary.toString()}
                                  size={12}
                                >
                                  {t('help.mission-in-language-only', { cultures: missionDetail.dto.supportedCultureCodes?.map((x) => t(x)).join(', ') })}
                                </MoreInfoButton>
                              </View>
                            )}
                            <View style={[styles.missionsBranchDetail.content.infoContainer.summary, missionSummaryContainerStyle]}>
                              {false && missionDetail.dto.tags != undefined && missionDetail.dto.tags != '' && (
                                <View style={[styles.missionsBranchDetail.content.infoContainer.summaryItem, missionSummaryStyle]}>
                                  <FontAwesomeIcon
                                    icon={faStore}
                                    color={theme.text.primary.toString()}
                                    size={20}
                                    style={styles.missionsBranchDetail.content.infoContainer.summaryItemIcon}
                                  />
                                  <Label variant={LabelType.titleDarkSmall} style={{ marginLeft: 6, fontWeight: '700' }}>
                                    {missionDetail.dto.tags}
                                  </Label>
                                </View>
                              )}

                              <View style={[styles.missionsBranchDetail.content.infoContainer.summaryItem, missionSummaryStyle]}>
                                <FontAwesomeIcon
                                  icon={faAward}
                                  color={summaryIconColor}
                                  style={styles.missionsBranchDetail.content.infoContainer.summaryItemIcon}
                                  size={20}
                                />
                                <Label
                                  variant={LabelType.valueLargeMission}
                                  numberOfLines={1}
                                  style={{ marginLeft: 6, minWidth: 70, flexShrink: 0, ...(titleTextColor ? { color: titleTextColor } : {}) }}
                                >
                                  {formatCurrencyValue(missionDetail.dto.salaryAmount)}
                                </Label>
                                <View style={styles.missionsBranchDetail.content.infoContainer.summaryItemValueSecondary}>
                                  <Label
                                    variant={LabelType.titleDarkSmall}
                                    style={{ marginLeft: 6, fontWeight: '700', ...(titleTextColor ? { color: titleTextColor } : {}) }}
                                  >
                                    {t('salary')}
                                  </Label>
                                  <Label
                                    variant={LabelType.titleDarkSmall}
                                    style={{ marginLeft: 6, fontWeight: '700', ...(titleTextColor ? { color: titleTextColor } : {}) }}
                                  >
                                    ({missionDetail.dto.salaryPoints ?? 0} pts)
                                  </Label>
                                </View>
                                <MoreInfoButton
                                  {...drawerMoreInfoButtonProps}
                                  style={{ alignSelf: 'flex-start', marginLeft: 8 }}
                                  title={''}
                                  color={bodyTextColor}
                                  openURL={'rewards-link-lanla-website-support'}
                                >
                                  {`${t('help.points-rate')}`}
                                </MoreInfoButton>
                              </View>

                              {missionDetail.dto.requiredPurchase && (
                                <View style={[styles.missionsBranchDetail.content.infoContainer.summaryItem, missionSummaryStyle]}>
                                  <FontAwesomeIcon
                                    icon={faBasketShopping}
                                    color={summaryIconColor}
                                    style={styles.missionsBranchDetail.content.infoContainer.summaryItemIcon}
                                    size={20}
                                  />
                                  <Label
                                    variant={LabelType.titleDarkSmall}
                                    style={{ marginLeft: 6, fontWeight: '700', ...(titleTextColor ? { color: titleTextColor } : {}) }}
                                  >
                                    {t('purchase_required')}
                                  </Label>
                                </View>
                              )}
                              {missionDetail.dto.reimbursementValue != undefined && missionDetail.dto.reimbursementValue != '' && (
                                <View style={[styles.missionsBranchDetail.content.infoContainer.summaryItem, missionSummaryStyle]}>
                                  <FontAwesomeIcon
                                    icon={faReceipt}
                                    color={summaryIconColor}
                                    style={styles.missionsBranchDetail.content.infoContainer.summaryItemIcon}
                                    size={20}
                                  />
                                  <View style={styles.missionsBranchDetail.content.infoContainer.summaryItemValue}>
                                    <Label
                                      variant={LabelType.valueLargeMission}
                                      style={{ marginLeft: 6, minWidth: 50, ...(titleTextColor ? { color: titleTextColor } : {}) }}
                                    >
                                      {`${missionDetail.dto.reimbursementValue}`}
                                    </Label>
                                    <View style={styles.missionsBranchDetail.content.infoContainer.summaryItemValueSecondary}>
                                      <Label
                                        variant={LabelType.titleDarkSmall}
                                        style={{ marginLeft: 6, fontWeight: '700', ...(titleTextColor ? { color: titleTextColor } : {}) }}
                                      >
                                        {`${t('reimbursement-up-to')} ${missionDetail.dto.reimbursementValue}`}
                                      </Label>
                                      {missionDetail.dto.reimbursementMaxAmount > 0 && (
                                        <Label
                                          variant={LabelType.titleDarkSmall}
                                          style={{ marginLeft: 6, fontWeight: '700', ...(titleTextColor ? { color: titleTextColor } : {}) }}
                                        >
                                          Max {missionDetail.dto.reimbursementMaxAmount} $ ({missionDetail.dto.reimbursementMaxPoints} pts)
                                        </Label>
                                      )}
                                    </View>
                                  </View>
                                  {shouldShowReimbursementMoreInfo && (
                                    <MoreInfoButton
                                      {...drawerMoreInfoButtonProps}
                                      style={{ alignSelf: 'flex-start', marginLeft: 8 }}
                                      title={''}
                                      color={bodyTextColor}
                                    >
                                      {isDollarReimbursement ? pointsRateDollarsText : pointsRatePercentageText}
                                    </MoreInfoButton>
                                  )}
                                </View>
                              )}
                              {missionDetail.dto.time && (
                                <View style={[styles.missionsBranchDetail.content.infoContainer.summaryItem, missionSummaryStyle]}>
                                  <View style={{ flexDirection: 'column', flexGrow: 1, flexShrink: 1 }}>
                                    <View style={{ flexDirection: 'row', alignItems: 'center', flexGrow: 1, flexShrink: 1 }}>
                                      <FontAwesomeIcon
                                        icon={faCalendar}
                                        color={summaryIconColor}
                                        style={styles.missionsBranchDetail.content.infoContainer.summaryItemIcon}
                                        size={20}
                                      />
                                      <Label
                                        variant={LabelType.titleDarkSmall}
                                        style={{
                                          marginLeft: 6,
                                          fontWeight: '700',
                                          flexShrink: 1,
                                          textAlign: 'left',
                                          ...(titleTextColor ? { color: titleTextColor } : {}),
                                        }}
                                        numberOfLines={10}
                                      >
                                        {missionDetail.dto.time}
                                      </Label>
                                    </View>
                                    {missionDetail.dto.storeLocatorLink != undefined && (
                                      <Pressable
                                        style={{ marginLeft: 26 }}
                                        onPress={() => {
                                          Linking.openURL(missionDetail.dto.storeLocatorLink);
                                        }}
                                      >
                                        <Label
                                          style={{
                                            fontSize: 13,
                                            color: linkTextColor,
                                            flexShrink: 1,
                                            textAlign: 'left',
                                            textDecorationLine: 'underline',
                                          }}
                                        >
                                          {t('Store-locator-link-text')}
                                        </Label>
                                      </Pressable>
                                    )}
                                  </View>
                                </View>
                              )}
                              {(() => {
                                const missionStatus = acceptMissionViewModels?.find(
                                  (x) => x.periodMissionConfigurationId === missionDetail.dto.periodMissionConfigurationId,
                                );

                                if (missionStatus?.ageRestrictionType !== AgeRestrictionType.Minor || !missionStatus.requiresRelatedMinorSelection) {
                                  return null;
                                }

                                return (
                                  <View style={[styles.missionsBranchDetail.content.infoContainer.summaryItem, missionSummaryStyle]}>
                                    <FontAwesomeIcon
                                      icon={faChild}
                                      color={summaryIconColor}
                                      style={styles.missionsBranchDetail.content.infoContainer.summaryItemIcon}
                                      size={20}
                                    />
                                    <Label
                                      variant={LabelType.titleDarkSmall}
                                      style={{
                                        marginLeft: 6,
                                        fontWeight: '700',
                                        flexShrink: 1,
                                        textAlign: 'left',
                                        ...(titleTextColor ? { color: titleTextColor } : {}),
                                      }}
                                      numberOfLines={4}
                                    >
                                      {t('related-minor-required')}
                                    </Label>
                                  </View>
                                );
                              })()}
                              {acceptMissionViewModels?.find((x) => x.periodMissionConfigurationId === missionDetail.dto.periodMissionConfigurationId)
                                ?.ageRestrictionType === AgeRestrictionType.Adult && (
                                <View style={[styles.missionsBranchDetail.content.infoContainer.summaryItem, missionSummaryStyle]}>
                                  <FontAwesomeIcon
                                    icon={faUserSecret}
                                    color={summaryIconColor}
                                    style={styles.missionsBranchDetail.content.infoContainer.summaryItemIcon}
                                    size={20}
                                  />
                                  <Label
                                    variant={LabelType.titleDarkSmall}
                                    style={{
                                      marginLeft: 6,
                                      fontWeight: '700',
                                      flexShrink: 1,
                                      textAlign: 'left',
                                      ...(titleTextColor ? { color: titleTextColor } : {}),
                                    }}
                                    numberOfLines={4}
                                  >
                                    {t('adult-age-restriction-required')}
                                  </Label>
                                </View>
                              )}
                            </View>
                            {missionDetail.dto.details.find((x) => x.type === MissionConfigurationDetailType.Objective) &&
                              missionDetail.dto.details.find((x) => x.type === MissionConfigurationDetailType.Objective)?.text !== '' && (
                                <View style={styles.missionsBranchDetail.content.infoContainer.infoBlocksBase}>
                                  <View style={styles.missionsBranchDetail.content.infoContainer.infoBlocks.title}>
                                    <FontAwesomeIcon
                                      icon={faBullseye}
                                      style={styles.missionsBranchDetail.content.infoContainer.infoBlocks.titleIcon}
                                      color={titleIconColor}
                                    />
                                    <Label variant={LabelType.titleDark} style={titleTextColor ? { color: titleTextColor } : undefined}>
                                      {t('Your-objective')}
                                    </Label>
                                    <MoreInfoButton {...drawerMoreInfoButtonProps} style={{ marginLeft: 8 }} title={''} color={bodyTextColor}>
                                      {`${t('help.your-objective')}`}
                                    </MoreInfoButton>
                                  </View>
                                  <View style={styles.missionsBranchDetail.content.infoContainer.infoBlocks.content}>
                                    <CustomMarkdown style={bodyTextColor ? { body: { color: bodyTextColor, lineHeight: 16 } } : undefined}>
                                      {missionDetail.dto.details.find((x) => x.type === MissionConfigurationDetailType.Objective)?.text}
                                    </CustomMarkdown>
                                  </View>
                                </View>
                              )}
                            {missionDetail.dto.details.find((x) => x.type === MissionConfigurationDetailType.ToNote) &&
                              missionDetail.dto.details.find((x) => x.type === MissionConfigurationDetailType.ToNote)?.text !== '' && (
                                <View style={styles.missionsBranchDetail.content.infoContainer.infoBlocksBase}>
                                  <View style={styles.missionsBranchDetail.content.infoContainer.infoBlocks.title}>
                                    <FontAwesomeIcon
                                      icon={faPenNib}
                                      style={styles.missionsBranchDetail.content.infoContainer.infoBlocks.titleIcon}
                                      color={titleIconColor}
                                    />
                                    <Label variant={LabelType.titleDark} style={titleTextColor ? { color: titleTextColor } : undefined}>
                                      {t('Please-note')}
                                    </Label>
                                    <MoreInfoButton {...drawerMoreInfoButtonProps} style={{ marginLeft: 8 }} title={''} color={bodyTextColor}>
                                      {`${t('help.please-note')}`}
                                    </MoreInfoButton>
                                  </View>
                                  <View style={styles.missionsBranchDetail.content.infoContainer.infoBlocks.content}>
                                    <CustomMarkdown style={bodyTextColor ? { body: { color: bodyTextColor, lineHeight: 16 } } : undefined}>
                                      {missionDetail.dto.details.find((x) => x.type === MissionConfigurationDetailType.ToNote)?.text}
                                    </CustomMarkdown>
                                  </View>
                                </View>
                              )}
                            {missionDetail.dto.details.find((x) => x.type === MissionConfigurationDetailType.NiceToKnow) &&
                              missionDetail.dto.details.find((x) => x.type === MissionConfigurationDetailType.NiceToKnow)?.text !== '' && (
                                <View style={styles.missionsBranchDetail.content.infoContainer.infoBlocksBase}>
                                  <View style={styles.missionsBranchDetail.content.infoContainer.infoBlocks.title}>
                                    <FontAwesomeIcon
                                      icon={faTriangleExclamation}
                                      style={{ ...styles.missionsBranchDetail.content.infoContainer.infoBlocks.titleIcon, width: 24, height: 24 }}
                                      color={titleIconColor}
                                    />
                                    <Label variant={LabelType.titleDark} style={titleTextColor ? { color: titleTextColor } : undefined}>
                                      {t('Pay-attention')}
                                    </Label>
                                    <MoreInfoButton {...drawerMoreInfoButtonProps} style={{ marginLeft: 8 }} title={''} color={bodyTextColor}>
                                      {`${t('help.nice-to-know')}`}
                                    </MoreInfoButton>
                                  </View>
                                  <View style={styles.missionsBranchDetail.content.infoContainer.infoBlocks.content}>
                                    <CustomMarkdown style={bodyTextColor ? { body: { color: bodyTextColor, lineHeight: 16 } } : undefined}>
                                      {missionDetail.dto.details.find((x) => x.type === MissionConfigurationDetailType.NiceToKnow)?.text}
                                    </CustomMarkdown>
                                  </View>
                                </View>
                              )}
                            {missionDetail.dto.details.find((x) => x.type === MissionConfigurationDetailType.Scenario) &&
                              missionDetail.dto.details.find((x) => x.type === MissionConfigurationDetailType.Scenario)?.text !== '' && (
                                <View style={styles.missionsBranchDetail.content.infoContainer.infoBlocksBase}>
                                  <View style={styles.missionsBranchDetail.content.infoContainer.infoBlocks.title}>
                                    <FontAwesomeIcon
                                      icon={faUserSecret}
                                      style={styles.missionsBranchDetail.content.infoContainer.infoBlocks.titleIcon}
                                      color={titleIconColor}
                                    />
                                    <Label variant={LabelType.titleDark} style={titleTextColor ? { color: titleTextColor } : undefined}>
                                      {t('The-scenario')}
                                    </Label>
                                    <MoreInfoButton
                                      {...drawerMoreInfoButtonProps}
                                      style={{ marginLeft: 8 }}
                                      title={''}
                                      color={bodyTextColor}
                                      openURL={'the-scenario-lanla-website-support-link'}
                                    >
                                      {t('help.the-scenario')}
                                    </MoreInfoButton>
                                  </View>
                                  <View style={styles.missionsBranchDetail.content.infoContainer.infoBlocks.content}>
                                    <CustomMarkdown style={bodyTextColor ? { body: { color: bodyTextColor, lineHeight: 16 } } : undefined}>
                                      {missionDetail.dto.details.find((x) => x.type === MissionConfigurationDetailType.Scenario)?.text}
                                    </CustomMarkdown>
                                  </View>
                                </View>
                              )}
                            {eligibilityText !== '' && renderMissionDetailAccordion('admissibility', t('Admissibility'), faCheckDouble, eligibilityText)}
                            {termsAndConditionsText !== '' &&
                              renderMissionDetailAccordion('conditions', t('Terms-and-Conditions'), faReceipt, termsAndConditionsText)}
                            <View style={styles.missionsBranchDetail.content.infoContainer.infoBlocks.buttonContainer}>
                              {(() => {
                                const currentMissionStatus = authStore.logged !== LoggedState.Anonymous && acceptMissionViewModel;
                                const currentMissionStatusText = missionStatusText(currentMissionStatus);
                                const showIdVerificationCta = shouldShowIdVerificationCta(currentMissionStatus);
                                const statusMessageColor = showIdVerificationCta
                                  ? (lightTheme.text?.darkTurquoiseTwo?.toString() ?? '#035164')
                                  : isAirportMission
                                    ? (lightTheme.colorErrorPrimary?.toString() ?? '#8e183d')
                                    : theme.colorErrorPrimary.toString();

                                if (!currentMissionStatusText) {
                                  return null;
                                }

                                return (
                                  <View
                                    style={{
                                      paddingHorizontal: 16,
                                      width: '100%',
                                    }}
                                  >
                                    <View
                                      style={{
                                        backgroundColor: isAirportMission
                                          ? (lightTheme.colorErrorPrimaryBackground?.toString() ?? '#ffffff')
                                          : theme.colorErrorPrimaryBackground,
                                        ...StylesHelper.padding(12, 16),
                                        ...StylesHelper.borderRadius(8),
                                        flexGrow: 1,
                                      }}
                                    >
                                      <View style={{ flexDirection: 'row', gap: 16, alignItems: 'center' }}>
                                        <FontAwesomeIcon
                                          style={{ flexShrink: 0 }}
                                          icon={faCircleExclamation}
                                          size={28}
                                          color={statusMessageColor}
                                        />
                                        <View style={{ flexShrink: 1 }}>
                                          {/* <Label style={{ fontSize: 14, color: theme.colorErrorPrimary, fontWeight: '600' }}>{t('This-mission-is-not-accessible')}</Label> */}
                                          <Label
                                            style={{
                                              color: statusMessageColor,
                                              flexShrink: 1,
                                            }}
                                            numberOfLines={5}
                                          >
                                            {currentMissionStatusText}
                                          </Label>
                                        </View>
                                      </View>
                                      {showIdVerificationCta && (
                                        <>
                                          <CustomButton
                                            title={t('id-verification')}
                                            onPress={openIdVerificationPanel}
                                            style={
                                              isAirportMission
                                                ? {
                                                    marginTop: 12,
                                                    alignSelf: 'center',
                                                    width: 220,
                                                    maxWidth: '100%',
                                                    backgroundColor: airportTitleColor,
                                                    borderColor: airportTitleColor,
                                                  }
                                                : { marginTop: 12, alignSelf: 'center', width: 220, maxWidth: '100%' }
                                            }
                                            textStyle={isAirportMission ? { color: '#ffffff' } : undefined}
                                          />
                                          <Pressable accessibilityRole="link" onPress={openIdVerificationHelp} style={{ marginTop: 12, alignSelf: 'center', padding: 4 }}>
                                            <Label style={{ color: isAirportMission ? airportTitleColor : statusMessageColor, fontSize: 16, lineHeight: 20 }}>
                                              {t('learn-more')}
                                            </Label>
                                          </Pressable>
                                        </>
                                      )}
                                    </View>
                                  </View>
                                );
                              })()}
                              {(shouldShowAcceptMissionButton || shouldShowReferMissionButton) && (
                                <>
                                  {shouldShowAcceptMissionButton && (
                                    <View style={{ flexDirection: 'row' }}>
                                      <CustomButton
                                        disabled={acceptLoading || acceptMission.isPending}
                                        style={
                                          isAirportMission
                                            ? { backgroundColor: airportTitleColor, borderColor: airportTitleColor, minWidth: 165 }
                                            : { minWidth: 165 }
                                        }
                                        textStyle={[
                                          styles.missionsBranchDetail.content.infoContainer.infoBlocks.button,
                                          isAirportMission ? { color: '#ffffff' } : {},
                                        ]}
                                        title={t('accept_the_mission')}
                                        onPress={() => {
                                          needToKnowMoreAboutUserPopup(missionDetail.dto.periodMissionConfigurationId);
                                        }}
                                      />
                                    </View>
                                  )}
                                  {shouldShowReferMissionButton && (
                                    <View style={{ flexDirection: 'row' }}>
                                      <CustomButton
                                        style={{ backgroundColor: '#6350ad', borderColor: '#6350ad', minWidth: 165 }}
                                        textStyle={[styles.missionsBranchDetail.content.infoContainer.infoBlocks.button, { color: '#ffffff' }]}
                                        title={t('refer-mission-button')}
                                        onPress={() => openMissionReferPopup(missionDetail.dto)}
                                      />
                                    </View>
                                  )}
                                </>
                              )}
                            </View>
                          </>
                        )}
                      </View>
                    );
                  })}
                </>
              ) : branchDetailQuery.isError ? (
                <View
                  style={{ flexGrow: 1, marginBottom: 24, marginTop: 12, paddingBottom: 12, minHeight: 120, alignItems: 'center', justifyContent: 'center' }}
                >
                  <Label style={{ fontSize: 16, fontWeight: '600', textAlign: 'center' }}>{t('AcceptMissionRetry')}</Label>
                  <Pressable onPress={() => branchDetailQuery.refetch()} style={{ marginTop: 12 }}>
                    <Label variant={LabelType.caption} style={{ textTransform: 'lowercase' }}>
                      {t('retry')}
                    </Label>
                  </Pressable>
                </View>
              ) : (
                <View style={{ flexGrow: 1, marginBottom: 24, marginTop: 12, paddingBottom: 12, height: 100 }}>
                  <Loading />
                </View>
              )}
            </View>
          )}
        </>
      )}
      {loading && <Loading />}
      {!props.profileManagerRef && <ProfileManager ref={localProfileManager} />}
    </ScrollView>
  );
};

export default MissionBranchList;
